import React, { useState, useEffect, useRef } from "react";
import ReactDOM, { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { init as initYeastarSDK } from "ys-webrtc-sdk-core";
import {
  initializeYeastarSDK,
  fetchYeastarSignature,
  resetYeastar,
} from "../../data/redux/slices/YeastarSlice";
import { showToast } from "../../data/redux/slices/ToastSlice";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  closeDialer,
  openDialer,
  clearNumber,
  setPhoneNumber as setDialerNumber,
  setCallerAndNumber,
} from "../../data/redux/slices/FloatingDialerSlice";
import { setBalance as setReduxBalance } from "../../data/redux/slices/CreditsSlice";
import {
  setPowerDialerSession,
  setPowerDialerNextNumber,
  clearPowerDialerNextNumber,
  clearPowerDialerSession,
  markCampaignCompleted,
} from "../../data/redux/slices/PowerDialerSlice";
import { setDefaultCallerNumber, fetchProfile } from "../../data/redux/slices/ProfileSlice";
import { showLoggedInElsewhereModal } from "../../data/redux/slices/LoggedInElsewhereSlice";
import { all_routes } from "../../../feature-module/router/all_routes";
import AfterCallOffcanvas from "../offCanvas/after-call/AfterCallOffcanvas";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./floatingDialer.css";
import api from "../../axios/axiosInstance";
import dayjs from "dayjs";
import {
  resolveCallFromNumber,
  resolveCallToNumber,
} from "../../utils/callNumberResolver";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Converts any raw number into a dialable format for the PBX.
 * Strips everything except digits.
 * UAE-to-UAE calls: 971XXXXXXXXX → 0XXXXXXXXX (only when caller is also UAE).
 * Non-UAE caller or unknown caller: destination left as-is.
 * Example (UAE caller): "+971501234567" → "0501234567"
 * Example (non-UAE caller): "+971501234567" → "971501234567"
 */
function toLocalDialFormat(raw, callerNumber = null) {
  const digits = (raw || "").replace(/\D/g, "");
  const callerDigits = (callerNumber || "").replace(/\D/g, "");
  const callerIsUAE = callerDigits.startsWith("971") && callerDigits.length >= 10;
  if (callerIsUAE && digits.startsWith("971") && digits.length >= 10) {
    return "0" + digits.slice(3);
  }
  return digits;
}

/**
 * Parses a raw number into { countryCode, number } for the API.
 * Tries libphonenumber first; falls back to splitting on "971" prefix.
 * Example: "+971501234567" → { countryCode: "971", number: "501234567" }
 */
function toApiPayload(raw) {
  // Normalise: ensure leading +
  let normalised = raw.trim();
  if (!normalised.startsWith("+")) normalised = `+${normalised}`;

  const parsed = parsePhoneNumberFromString(normalised);
  if (parsed) {
    return {
      phoneNumbers: {
        countryCode: parsed.countryCallingCode,   // e.g. "971"
        number: parsed.nationalNumber,             // e.g. "501234567"
      },
    };
  }

  // Fallback: strip + and split manually
  const digits = normalised.replace(/\D/g, "");
  if (digits.startsWith("971") && digits.length > 3) {
    return {
      phoneNumbers: {
        countryCode: "971",
        number: digits.slice(3),
      },
    };
  }

  return {
    phoneNumbers: {
      countryCode: "",
      number: digits,
    },
  };
}

function normaliseBillingNumber(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;
  const digits = value.replace(/\D/g, "");
  if (!digits) return null;
  return `+${digits}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function FloatingDialer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const route = all_routes;
  const userProfile = useSelector((state) => state.profile);
  const floatingDialer = useSelector((state) => state.floatingDialer);
  const yeastar = useSelector((state) => state.yeastar);
  const powerDialer = useSelector((state) => state.powerDialer);
  const callStartedAt = Date.now()
  const fromNumberRef = useRef(powerDialer.fromNumber);
  const checkMicrophoneAvailable = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch {
      return false;
    }
  };

  const [phoneStatus, setPhoneStatus] = useState(
    yeastar.status || "disconnected"
  );

  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(!!yeastar.phone);
  const offcanvasTriggerRef = useRef(null);
  const phoneRef = useRef(yeastar.phone);
  const pbxRef = useRef(yeastar.pbx);
  const destroyRef = useRef(yeastar.destroy);
  // Pool of all initialized SDK instances keyed by extension number
  const sdkPoolRef = useRef({});
  // Dedup concurrent inits: ext number → Promise (prevents double-init race)
  const initInProgressRef = useRef({});
  // Extensions evicted by ensureExtension before a call — re-registered after call ends
  const evictedExtensionsRef = useRef([]);
  // Tracks powerDialer.isActive without stale closure issues inside effects/handlers
  const powerDialerActiveRef = useRef(powerDialer.isActive);
  // Stable ref to the current incoming-call handler so secondary extensions can attach it
  const incomingHandlerRef = useRef(null);
  // Per-extension handler map: extensionNumber → handler fn (for proper cleanup)
  const extHandlersRef = useRef({});
  // Factory ref so initExtensionById (outside the useEffect) can create handlers
  const makeIncomingHandlerRef = useRef(null);
  // Keep assignedExtensions fresh for handlers created outside the incoming useEffect
  const assignedExtensionsRef = useRef([]);
  // Keep selectedCallerNumber and callerNumbers fresh for stale-closure handlers
  const selectedCallerNumberRef = useRef(null);
  const callerNumbersRef = useRef([]);
  // Extensions that have connected at least once — re-connects are silent (no toast, no yellow dot)
  const everConnectedRef = useRef(new Set());
  // Extension connection statuses: { [extensionNumber]: 'connecting'|'ready'|'failed' }
  const [extStatuses, setExtStatuses] = useState({});
  const setExtStatus = (extNum, status) =>
    setExtStatuses((prev) => ({ ...prev, [extNum]: status }));
  // Incremented whenever phoneRef.current changes so event-listener effects re-run
  const [phoneKey, setPhoneKey] = useState(0);
  // Incremented to force a retry of the primary SDK init (re-triggers the init useEffect)
  const [initRetryKey, setInitRetryKey] = useState(0);
  // Helper: update the active phone and trigger a re-render for listener effects
  const setActivePhone = (phone, pbx, destroy) => {
    console.log("[DEBUG] setActivePhone called | phone=", phone);
    phoneRef.current   = phone;
    pbxRef.current     = pbx;
    destroyRef.current = destroy;
    setPhoneKey((k) => k + 1);
  };

  const [currentSession, setCurrentSession] = useState(null);
  const [incomingSession, setIncomingSession] = useState(null);
  const [incomingExtension, setIncomingExtension] = useState(null); // { extensionNumber, PBX_TELEPHONE }
  const [callStatus, setCallStatus] = useState("");
  const [callDuration, setCallDuration] = useState(0);
  const [callInfo, setCallInfo] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [foundContactRecord, setFoundContactRecord] = useState(null);
  const [isManualDial, setIsManualDial] = useState(false);
  const [manualNumber, setManualNumber] = useState("");

  const [callPhoneNumber, setCallPhoneNumber] = useState(null);
  const skipOffcanvasRef = useRef(false);
  const [phoneNumber, setPhoneNumber] = useState("+971");
  const [dialpadInput, setDialpadInput] = useState("");

  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [showDialpad, setShowDialpad] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showRecents, setShowRecents] = useState(false);
  // Extension enabled: true if extensionStatus=true OR agent has assigned extensions
  // (mirrors backend yeastarLoginController: disabled only when status=false AND no assignedExtensions)
  const isExtensionEnabled = !!(
    userProfile?.extensionStatus ||
    (userProfile?.assignedExtensions || []).some((e) => e.enabled !== false)
  );

  // ─── Calling-number selector ────────────────────────────────────────────────
  const [callerNumbers, setCallerNumbers] = useState([]);
  const [callerNumbersLoaded, setCallerNumbersLoaded] = useState(false);
  const [selectedCallerNumber, setSelectedCallerNumber] = useState(null);
  const [callerDropdownOpen, setCallerDropdownOpen] = useState(false);
  const [callerDropdownPos, setCallerDropdownPos]   = useState({ top: 0, left: 0, width: 0 });
  const callerDropdownRef  = useRef(null);  // trigger wrapper (for click-outside)
  const callerTriggerRef   = useRef(null);  // trigger button (for getBoundingClientRect)
  const callerPortalRef    = useRef(null);  // portal dropdown list (for click-outside)

  const pbxTelephoneFromProfile = userProfile?.PBXDetails?.PBX_TELEPHONE || userProfile?.telephone;
  // Use whatever number the user has actively selected; fall back to profile PBX telephone
  const billingCallerNumber =
    normaliseBillingNumber(selectedCallerNumber) ||
    normaliseBillingNumber(pbxTelephoneFromProfile);
  const [recentDials, setRecentDials] = useState(() => {
    try { return JSON.parse(localStorage.getItem("voycell_recent_dials") || "[]"); }
    catch { return []; }
  });

  const saveRecentDial = (number, name = "", callerNumber = null) => {
    setRecentDials((prev) => {
      const entry = { number, name, ts: Date.now(), callerNumber };
      const filtered = prev.filter((r) => r.number !== number);
      const next = [entry, ...filtered].slice(0, 10);
      try { localStorage.setItem("voycell_recent_dials", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const dialRecentNumber = async (number) => {
    if (!isExtensionEnabled) {
      dispatch(showToast({ message: "Extension is disabled. Kindly contact administrator", variant: "danger" }));
      return;
    }
    flushSync(() => setDialingNumber(number));
    try {
      const fullNumber = number.startsWith("+") ? number : `+${number}`;
      const digits = fullNumber.replace(/\D/g, "");
      if (digits.length > 6) {
        try {
          const checkRes = await api.post("/billing/pre-call-check", {
            destination: fullNumber,
            selectedDIDNumber: billingCallerNumber,
          });
          if (!checkRes.data.canCall) {
            dispatch(showToast({
              message: `Insufficient balance — need $${checkRes.data.ratePerMin?.toFixed(4) ?? "?"}/min, have $${Number(checkRes.data.currentBalance ?? 0).toFixed(2)}`,
              variant: "danger",
            }));
            return;
          }
          if (checkRes.data.threshold !== undefined) liveBillingThresholdRef.current = checkRes.data.threshold;
          autoRechargeEnabledRef.current = !!checkRes.data.autoRechargeEnabled;
        } catch (err) {
        }
      }
      originalDialledNumberRef.current = fullNumber;
      liveDestinationRef.current = fullNumber;
      isOutboundCallRef.current  = true;
      const localNumber = toLocalDialFormat(number, selectedCallerNumber);
      if (!await ensureExtension()) return;
      await phoneRef?.current?.call(localNumber);
      setShowRecents(false);
    } finally {
      setDialingNumber(null);
    }
  };

  const handlePowerDialerAfterSave = async (disposition) => {
    const session = powerDialer.session;
    if (!session || !powerDialer.isActive) return;
    try {
      const res = await api.post(`/api/power-dialer/sessions/${session._id}/next`, {
        disposition: disposition || "",
        contact_status: "called",
      });
      const { contact, done } = res.data.data;
      if (done) {
        dispatch(clearPowerDialerSession());
        dispatch(markCampaignCompleted());
        dispatch(showToast({ message: "Power Dialer: All contacts called!", variant: "success" }));
      } else {
        dispatch(setPowerDialerSession({ session: res.data.data.session, contact }));
        if (contact?.phone) {
          dispatch(setPowerDialerNextNumber(contact.phone));
        }
      }
    } catch (err) {
    }
  };

  // ─── API-based recent calls ─────────────────────────────────────────────────
  const [recentCallsData, setRecentCallsData] = useState([]);
  const [recentCallsLoading, setRecentCallsLoading] = useState(false);
  const [recentRefreshing, setRecentRefreshing] = useState(false);
  const [recentAudioUrl, setRecentAudioUrl] = useState(null);
  const [recentPlayingKey, setRecentPlayingKey] = useState(null);
  const [recentAudioLoading, setRecentAudioLoading] = useState(null);
  const [recentPage, setRecentPage] = useState(1);
  const [recentTotal, setRecentTotal] = useState(0);
  const [recentSearch, setRecentSearch] = useState("");
  const [selectedCallDetail, setSelectedCallDetail] = useState(null); // number history panel
  const [numHistoryData, setNumHistoryData] = useState([]);
  const [numHistoryLoading, setNumHistoryLoading] = useState(false);
  const [numHistoryPage, setNumHistoryPage] = useState(1);
  const [numHistoryTotal, setNumHistoryTotal] = useState(0);
  const [numHistoryAudioUrl, setNumHistoryAudioUrl] = useState(null);
  const [numHistoryPlayingKey, setNumHistoryPlayingKey] = useState(null);
  const [numHistoryAudioLoading, setNumHistoryAudioLoading] = useState(null);
  const [dialingNumber, setDialingNumber] = useState(null);
  const [isDialpadCalling, setIsDialpadCalling] = useState(false);
  const NUM_PAGE_SIZE = 10;
  const recentSearchTimer = useRef(null);

  const PAGE_SIZE = 10;

  const fetchRecentCalls = async (page = 1, search = "") => {
    setRecentCallsLoading(true);
    try {
      const role = userProfile?.role;
      const endpoint = role === "user" ? "/call/agent-call-history" : "/call/company-call-history";
      const res = await api.post(endpoint, { page, page_size: PAGE_SIZE, search });
      setRecentCallsData(res.data?.callRecords || []);
      setRecentTotal(res.data?.totalRecords || 0);
    } catch (err) {
    } finally {
      setRecentCallsLoading(false);
    }
  };

  const handleRefreshRecents = async () => {
    setRecentRefreshing(true);
    try {
      await api.post("call/fetch-and-store");
      // Clear temporary localStorage dials — they'll now appear from the API
      localStorage.removeItem("voycell_recent_dials");
      setRecentDials([]);
      setRecentPage(1);
      await fetchRecentCalls(1, recentSearch);
    } catch (err) {
    } finally {
      setRecentRefreshing(false);
    }
  };

  const handlePlayRecentRecording = async (fileName, key) => {
    if (recentPlayingKey === key) {
      setRecentAudioUrl(null);
      setRecentPlayingKey(null);
      return;
    }
    setRecentAudioLoading(key);
    try {
      const res = await api.post("/call/recording", { record_file: fileName });
      if (res.data?.fileUrl) {
        setRecentAudioUrl(res.data.fileUrl);
        setRecentPlayingKey(key);
      }
    } catch (err) {
    } finally {
      setRecentAudioLoading(null);
    }
  };


  // For agents (role "user"): populate caller numbers from assignedCallerNumbers,
  // mapping each telephone back to its extensionNumber via assignedExtensions + PBXDetails.
  useEffect(() => {
    if (userProfile?.role !== "user") return;
    // Build set of telephone numbers belonging to disabled extensions — used to
    // filter assignedCallerNumbers so disabled extensions don't appear in the dialer.
    const disabledTels = new Set(
      (userProfile?.assignedExtensions || [])
        .filter((e) => e.enabled === false)
        .map((e) => e.PBX_TELEPHONE)
        .filter(Boolean)
    );

    const assigned = (userProfile?.assignedCallerNumbers || []).filter((num) => !disabledTels.has(num));

    // Build telephone → extensionNumber lookup
    const extMap = new Map();
    for (const e of (userProfile?.assignedExtensions || [])) {
      if (e.enabled === false) continue;
      if (e.PBX_TELEPHONE && e.extensionNumber) extMap.set(e.PBX_TELEPHONE, e.extensionNumber);
    }
    const pbxTel = userProfile?.PBXDetails?.PBX_TELEPHONE;
    const pbxExt = userProfile?.PBXDetails?.PBX_EXTENSION_NUMBER;
    if (pbxTel && pbxExt) extMap.set(pbxTel, pbxExt);

    const all = assigned.map((num) => {
      const extNum = extMap.get(num);
      return { number: num, isExtension: !!extNum, extensionNumber: extNum || null };
    });

    // Include top-level telephone / PBXDetails telephone if not already in the list
    const tel    = userProfile?.telephone || pbxTel;
    const extNum = userProfile?.extensionNumber || pbxExt;
    if (tel && !assigned.includes(tel) && !disabledTels.has(tel)) {
      all.unshift({ number: tel, isExtension: !!extNum, extensionNumber: extNum || null });
    }

    // Include any assignedExtensions telephones not yet in the list
    // (covers extensions assigned via superadmin that bypass assignedCallerNumbers)
    for (const e of (userProfile?.assignedExtensions || [])) {
      if (e.enabled === false) continue;
      if (e.PBX_TELEPHONE && !all.find((n) => n.number === e.PBX_TELEPHONE)) {
        all.push({ number: e.PBX_TELEPHONE, isExtension: true, extensionNumber: e.extensionNumber || null });
      }
    }

    setCallerNumbers(all);
    setCallerNumbersLoaded(true);
    const preferred = userProfile?.defaultCallerNumber;
    if (!selectedCallerNumber || !all.find((n) => n.number === selectedCallerNumber)) {
      if (preferred && all.find((n) => n.number === preferred)) {
        setSelectedCallerNumber(preferred);
      } else if (all.length > 0) {
        setSelectedCallerNumber(all[0].number);
      }
    }
  }, [userProfile?.assignedCallerNumbers, userProfile?.telephone, userProfile?.assignedExtensions, userProfile?.PBXDetails?.PBX_TELEPHONE]); // eslint-disable-line react-hooks/exhaustive-deps

  // For company admins: seed callerNumbers immediately from profile extensions so
  // the FROM selector appears as soon as the profile loads, before phone is ready.
  useEffect(() => {
    if (!userProfile?.id || userProfile?.isLoading || userProfile?.role === "user") return;
    const seeded = (userProfile.assignedExtensions || [])
      .filter((e) => e.enabled !== false && e.inAdminPool !== false)
      .map((e) => ({ number: e.PBX_TELEPHONE, isExtension: true, extensionNumber: e.extensionNumber }));
    setCallerNumbers((prev) => (prev.length > 0 ? prev : seeded));
    setCallerNumbersLoaded(true);
  }, [userProfile?.id, userProfile?.isLoading, userProfile?.assignedExtensions]); // eslint-disable-line react-hooks/exhaustive-deps

  // For company admins: fetch available calling numbers once when the PBX connects.
  // Numbers assigned to agents are excluded — those belong to the agent's pool.
  useEffect(() => {
    if (userProfile?.role === "user") return; // agents use the effect above
    if (phoneStatus !== "ready" && phoneStatus !== "talking") return;
    Promise.all([
      api.get("/didlogic/numbers/calling-numbers"),
      api.get("/admin/user/getAgent"),
    ])
      .then(([numsRes, agentsRes]) => {
        const purchased = numsRes.data?.data || [];
        const agents    = agentsRes.data?.data || [];

        // Build set of all telephone numbers currently assigned to agents
        const assignedToAgents = new Set(
          agents.flatMap((a) => a.assignedCallerNumbers || [])
        );

        // Superadmin-assigned extensions — exclude non-multichannel ones given to agents
        // Multichannel extensions (channels > 1) are shared, so keep them in admin's list too
        // Also exclude any where admin removed themselves (inAdminPool: false)
        const assignedExts = (userProfile?.assignedExtensions || [])
          .filter((e) => {
            if (e.enabled === false) return false;
            if (e.inAdminPool === false) return false;
            const isMultichannel = (e.channels || 1) > 1;
            return isMultichannel || !assignedToAgents.has(e.PBX_TELEPHONE);
          })
          .map((e) => ({
            number:          e.PBX_TELEPHONE,
            isExtension:     true,
            extensionNumber: e.extensionNumber,
          }));

        // Fall back to the single PBX telephone only if no assigned extensions exist
        const extTelephone = userProfile?.telephone;
        const extNumber    = userProfile?.extensionNumber;
        const assignedNumbers = new Set(assignedExts.map((e) => e.number));
        const pbxEntry =
          extTelephone && !assignedNumbers.has(extTelephone) && !assignedToAgents.has(extTelephone)
            ? [{ number: extTelephone, isExtension: true, extensionNumber: extNumber || "" }]
            : [];

        let all = [
          ...purchased.filter((n) => !assignedToAgents.has(n.number)).map((n) => ({ ...n })),
          ...assignedExts,
          ...pbxEntry,
        ];

        // Deduplicate: if the same number exists as both a DID and a PBX extension
        // (e.g., 043857306 purchased from DIDLogic AND assigned as PBX_TELEPHONE for ext 1002),
        // prefer the extension entry so ensureExtension can switch SIP registrations correctly.
        const deduped = new Map();
        for (const entry of all) {
          const existing = deduped.get(entry.number);
          if (!existing || (!existing.isExtension && entry.isExtension)) {
            deduped.set(entry.number, entry);
          }
        }
        all = [...deduped.values()];

        // Destroy any SDK instances that were already warm-started for agent-assigned extensions
        (userProfile?.assignedExtensions || []).forEach((ext) => {
          if (assignedToAgents.has(ext.PBX_TELEPHONE)) {
            const inst = sdkPoolRef.current[ext.extensionNumber];
            if (inst) {
              if (incomingHandlerRef.current) {
                try { inst.phone?.removeListener("incoming", incomingHandlerRef.current); } catch {}
              }
              try { inst.destroy?.(); } catch {}
              delete sdkPoolRef.current[ext.extensionNumber];
            }
          }
        });

        setCallerNumbers(all);
        setCallerNumbersLoaded(true);

        // Pre-select: defaultCallerNumber → defaultCallerDID (legacy) → first entry
        const preferred = userProfile?.defaultCallerNumber || userProfile?.defaultCallerDID;
        if (!selectedCallerNumber) {
          if (preferred && all.find((n) => n.number === preferred)) {
            setSelectedCallerNumber(preferred);
          } else if (all.length > 0) {
            setSelectedCallerNumber(all[0].number);
          }
        }
      })
      .catch(() => {});
  }, [phoneStatus, userProfile?.assignedExtensions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close caller dropdown on outside click (checks both trigger wrapper and portal list)
  useEffect(() => {
    const handleOutside = (e) => {
      const inTrigger = callerDropdownRef.current?.contains(e.target);
      const inPortal  = callerPortalRef.current?.contains(e.target);
      if (!inTrigger && !inPortal) setCallerDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Returns the display label for a caller number entry based on the showCallerName mode:
  //   'name'        → nickname only (falls back to number if no name set)
  //   'name_number' → "nickname (number)" (falls back to number if no name set)
  //   'number' / anything else → raw number
  // didLabels (company admin) takes priority over assignedExtensions[].nickname (superadmin).
  const getCallerLabel = (n) => {
    const mode = userProfile?.showCallerName;
    if (mode === "name" || mode === "name_number") {
      const didLabel = (userProfile?.didLabels || []).find((d) => d.number === n.number);
      const nickname = didLabel?.nickname ||
        (n.isExtension
          ? (userProfile?.assignedExtensions || []).find((e) => e.PBX_TELEPHONE === n.number)?.nickname
          : null);
      if (nickname) {
        return mode === "name_number" ? `${nickname} (${n.number})` : nickname;
      }
    }
    return n.number;
  };

  // Keep refs in sync so event-listener closures always read current values
  useEffect(() => { selectedCallerNumberRef.current = selectedCallerNumber; }, [selectedCallerNumber]);
  useEffect(() => { callerNumbersRef.current = callerNumbers; }, [callerNumbers]);

  // When the user switches to a local-format caller (04XXXXXXXX), auto-strip the
  // 971 country code from whatever is already in the dial field.
  useEffect(() => {
    const callerDigits = (selectedCallerNumber || "").replace(/\D/g, "");
    if (!callerDigits.startsWith("0")) return;
    const digits = (phoneNumber || "").replace(/\D/g, "");
    if (digits.startsWith("971") && digits.length >= 11) {
      setPhoneNumber("0" + digits.slice(3));
    }
  }, [selectedCallerNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a call is triggered from an external page (contact/lead list) with a
  // pre-selected caller number, sync it into the dialer's local state.
  useEffect(() => {
    if (floatingDialer.callerNumber) {
      setSelectedCallerNumber(floatingDialer.callerNumber);
    }
  }, [floatingDialer.callerNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search → refetch
  useEffect(() => {
    if (!showRecents) return;
    clearTimeout(recentSearchTimer.current);
    recentSearchTimer.current = setTimeout(() => {
      setRecentPage(1);
      fetchRecentCalls(1, recentSearch);
    }, 400);
    return () => clearTimeout(recentSearchTimer.current);
  }, [recentSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch when Recents tab first opens
  useEffect(() => {
    if (showRecents && recentCallsData.length === 0 && !recentCallsLoading) {
      fetchRecentCalls(1, recentSearch);
    }
  }, [showRecents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page change
  const handleRecentPageChange = (newPage) => {
    setRecentPage(newPage);
    fetchRecentCalls(newPage, recentSearch);
    setRecentAudioUrl(null);
    setRecentPlayingKey(null);
  };

  const getDateLabel = (dateStr) => {
    const today = dayjs().format("YYYY-MM-DD");
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
    if (dateStr === today) return "Today";
    if (dateStr === yesterday) return "Yesterday";
    return dayjs(dateStr).format("MMM D, YYYY");
  };

  const formatCallDuration = (secs) => {
    if (!secs || secs === 0) return null;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const getCallMeta = (record) => {
    const dir = record.direction?.toLowerCase();
    const answered = record.status?.toLowerCase() === "answered";
    const isInbound = dir === "inbound";
    const isMissed = !answered;
    const inboundNumber = resolveCallFromNumber(record);
    const outboundNumber = resolveCallToNumber(record);
    const number = isInbound
      ? (inboundNumber || record.call_from || "—")
      : (outboundNumber || record.call_to || "—");
    // The number used by our side: for outbound = our DID (call_from), for inbound = DID that was called (call_to)
    const viaNumber = isInbound
      ? (outboundNumber || record.call_to || null)
      : (inboundNumber || record.call_from || null);
    return { isInbound, isMissed, number, answered, viaNumber };
  };

  // ─── Number-specific call history ─────────────────────────────────────────
  const fetchNumberHistory = async (number, page = 1) => {
    setNumHistoryLoading(true);
    try {
      const normalised = number.startsWith("+") ? number : `+${number}`;
      const { phoneNumbers } = toApiPayload(normalised);
      const res = await api.post("/call/phone-number-call-history", {
        phonenumbers: [phoneNumbers],
        page,
        page_size: NUM_PAGE_SIZE,
      });
      setNumHistoryData(res.data?.callRecords || []);
      setNumHistoryTotal(res.data?.totalRecords || 0);
      setNumHistoryPage(page);
    } catch (err) {
    } finally {
      setNumHistoryLoading(false);
    }
  };

  const openNumberHistory = (number, name = "") => {
    setSelectedCallDetail({ number, name });
    setNumHistoryData([]);
    setNumHistoryPage(1);
    setNumHistoryTotal(0);
    setNumHistoryAudioUrl(null);
    setNumHistoryPlayingKey(null);
    fetchNumberHistory(number, 1);
  };

  const handleNumHistoryRecording = async (fileName, key) => {
    if (numHistoryPlayingKey === key) {
      setNumHistoryAudioUrl(null);
      setNumHistoryPlayingKey(null);
      return;
    }
    setNumHistoryAudioLoading(key);
    try {
      const res = await api.post("/call/recording", { record_file: fileName });
      if (res.data?.fileUrl) {
        setNumHistoryAudioUrl(res.data.fileUrl);
        setNumHistoryPlayingKey(key);
      }
    } catch (err) {
    } finally {
      setNumHistoryAudioLoading(null);
    }
  };

  const [position, setPosition] = useState({
    x: Math.max(0, (window.innerWidth - 340) / 2),
    y: Math.max(0, (window.innerHeight - 600) / 2),
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const remoteAudioRef = useRef(null);
  const ringtoneRef = useRef(null);
  // ─── Notification permission state ──────────────────────────────────────────
  const notifRef        = useRef(null);
  const [notifPermission,  setNotifPermission]  = useState(
    typeof Notification !== "undefined" ? Notification.permission : "granted"
  );
  const [bannerDismissed,  setBannerDismissed]  = useState(false);
  const [blockedDismissed, setBlockedDismissed] = useState(false);

  // ─── Ringtone — muted autoplay trick ────────────────────────────────────────
  //
  // Chrome always allows MUTED autoplay with no user gesture whatsoever.
  // Unmuting an already-playing audio element also requires no gesture.
  //
  // So: start the ringtone muted on page load (always succeeds).
  //     When a call arrives → unmute. When it ends → mute + rewind.
  //     Zero user interaction needed, zero permissions needed.
  //
  useEffect(() => {
    const audio = ringtoneRef.current;
    if (!audio) return;
    audio.loop   = true;
    audio.muted  = true;   // muted autoplay is always allowed
    audio.volume = 1;
    audio.play().catch(() => {}); // should always succeed (muted)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRingtone = () => {
    const audio = ringtoneRef.current;
    if (!audio) return;
    audio.muted        = true;  // mute instead of pause — keeps it "playing" silently
    audio.currentTime  = 0;     // rewind so next ring starts from the beginning
  };

  const playRingtone = () => {
    const audio = ringtoneRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.muted       = false;  // unmute — no gesture needed since it's already playing
  };

  // Mute / unmute whenever incomingSession changes
  useEffect(() => {
    if (incomingSession) {
      playRingtone();
    } else {
      stopRingtone();
    }
  }, [incomingSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Banner click: request notification permission ───────────────────────────
  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setNotifPermission(result);
  };

  // ─── Browser notification for incoming calls ─────────────────────────────────
  const showCallNotification = (session) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const name   = session?.status?.name   || "";
    const number = session?.status?.number || "Unknown";
    if (notifRef.current) notifRef.current.close();
    const notif = new Notification("📞 Incoming Call", {
      body:               name ? `${name}  •  ${number}` : number,
      icon:               "/assets/img/voycell-logo.png",
      requireInteraction: true,
      tag:                "incoming-call",
      silent:             false,
    });
    notif.onclick = () => { window.focus(); notif.close(); };
    notifRef.current = notif;
  };

  const closeCallNotification = () => {
    if (notifRef.current) { notifRef.current.close(); notifRef.current = null; }
  };
  /**
   * Stores the ORIGINAL number (with country code) that the user dialled.
   * handleStartSession uses this for the API call because session.status.number
   * only contains the local format (0XXXXXXXXX) that was passed to pbx.call().
   */
  const originalDialledNumberRef = useRef(null);

  // Tracks which extension number the SDK is currently initialized for
  const activeExtensionRef = useRef(yeastar.username || userProfile?.extensionNumber || null);

  // ─── Live billing refs ──────────────────────────────────────────────────────
  const liveBillingIntervalRef    = useRef(null);
  const currentCallIdRef          = useRef(null);   // Yeastar SDK callId from startSession
  const isOutboundCallRef         = useRef(false);  // true only for calls WE initiated
  const liveDestinationRef        = useRef(null);   // E.164 destination being billed
  const liveBillingThresholdRef   = useRef(5);      // auto-recharge threshold ($)
  const autoRechargeEnabledRef    = useRef(false);  // whether auto-recharge is on for this company

  // ─── Live billing state (shown inside active call screen) ──────────────────
  const [liveBalance,        setLiveBalance]        = useState(null);
  const [lowBalanceWarning,  setLowBalanceWarning]  = useState(false);
  const [liveBillingActive,  setLiveBillingActive]  = useState(false);

  // ─── Initialize Yeastar SDK ─────────────────────────────────────────────────
  // Shared init helper — deduplicates concurrent init calls for the same extension.
  // Multiple callers await the same Promise; only one HTTP round-trip is made.
  const initExtensionById = async (targetExt, silent = false) => {
    const stillAssigned = assignedExtensionsRef.current.some(
      (e) => String(e.extensionNumber) === String(targetExt) && e.inAdminPool !== false
    );
    if (!stillAssigned) return null;
    if (sdkPoolRef.current[targetExt]?.phone) {
      setExtStatus(targetExt, "ready");
      everConnectedRef.current.add(String(targetExt));
      return sdkPoolRef.current[targetExt];
    }
    if (initInProgressRef.current[targetExt]) {
      await initInProgressRef.current[targetExt];
      return sdkPoolRef.current[targetExt] || null;
    }
    if (!silent) setExtStatus(targetExt, "connecting");
    const promise = (async () => {
      const res = await api.post("/api/yeastar-login/get-signature", { extensionNumber: targetExt });
      if (res.data.status !== "success") throw new Error(res.data.message || "Signature failed");
      const { username, secret, pbxURL } = res.data.data;
      if (username !== targetExt) {
        throw new Error(`Backend returned credentials for ext ${username} instead of ${targetExt}. Contact your administrator.`);
      }
      const inst = await initYeastarSDK({ username, secret, pbxURL, enableLog: false });
      inst.phone.start();
      sdkPoolRef.current[targetExt] = inst;

      // Wait for SIP registration before returning so callers know the extension
      // is actually ready to place calls. Resolves immediately if already registered,
      // or on the 'registered' event, or after a 15 s timeout (non-fatal).
      if (!inst.phone.isRegistered) {
        await new Promise((resolve) => {
          const timer = setTimeout(() => {
            resolve();
          }, 15000);
          const onRegistered = () => {
            clearTimeout(timer);
            inst.phone.removeListener("registrationFailed", onFailed);
            resolve();
          };
          const onFailed = (e) => {
            clearTimeout(timer);
            inst.phone.removeListener("registered", onRegistered);
            resolve(); // resolve not reject — pool entry is set, let ensureExtension handle the unusable state
          };
          inst.phone.once("registered", onRegistered);
          inst.phone.once("registrationFailed", onFailed);
        });
      } else {
      }

      return inst;
    })();
    initInProgressRef.current[targetExt] = promise;
    try {
      const inst = await promise;
      setExtStatus(targetExt, "ready");
      everConnectedRef.current.add(String(targetExt));
      // Attach per-extension incoming listener if the factory is ready
      if (makeIncomingHandlerRef.current && inst?.phone) {
        const extEntry = assignedExtensionsRef.current.find((e) => e.extensionNumber === targetExt);
        const h = makeIncomingHandlerRef.current(targetExt, extEntry?.PBX_TELEPHONE || null);
        extHandlersRef.current[targetExt] = h;
        inst.phone.on("incoming", h);
      } else if (incomingHandlerRef.current && inst?.phone) {
        inst.phone.on("incoming", incomingHandlerRef.current);
      }
      return inst;
    } catch (err) {
      setExtStatus(targetExt, "failed");
      throw err;
    } finally {
      delete initInProgressRef.current[targetExt];
    }
  };

  // Warm-starts all non-primary assigned extensions that are NOT assigned to agents.
  // agentNums — Set of telephone numbers already given to agents (skip those).
  // Multichannel cloud extensions bypass the agentNums filter — admin keeps their own
  // channel and must initialize their SDK instance regardless of agent assignment.
  const initSecondaryExtensions = (primaryExtNum, agentNums = new Set()) => {
    const primaryDeviceId = (userProfile?.assignedExtensions || [])
      .find((e) => e.extensionNumber === primaryExtNum)
      ?.assignedDeviceId?.toString() ?? null;
    const allExts = (userProfile?.assignedExtensions || []).filter(
      (e) => {
        if (e.enabled === false) return false;
        if (e.extensionNumber === primaryExtNum) return false;
        if (e.inAdminPool === false) return false;
        // Skip extensions sharing a device with primary — they conflict on call and cause re-init delay.
        // Mark them "ready" so they show green dot (accessible on-demand via ensureExtension).
        const extDeviceId = e.assignedDeviceId?.toString() ?? null;
        if (primaryDeviceId && extDeviceId && extDeviceId === primaryDeviceId) {
          if (e.extensionNumber) setExtStatus(e.extensionNumber, "ready");
          return false;
        }
        const isMultichannel = (parseInt(e.channels, 10) || 1) > 1;
        return isMultichannel || !agentNums.has(e.PBX_TELEPHONE);
      }
    );
    setTimeout(() => allExts.forEach((ext) => {
      initExtensionById(ext.extensionNumber)
        .then(() => {})
        .catch((err) => {
          const isApiError = err?.code === -103 || err?.message === "PBX_API_ERROR";
          if (isApiError) {
            // PBX rejects first-connect — auto-retry once silently after 4s
            setTimeout(() => {
              delete sdkPoolRef.current[ext.extensionNumber];
              initExtensionById(ext.extensionNumber, true)
                .then(() => {})
                .catch((retryErr) => {
                  const msg = retryErr?.response?.data?.message || retryErr?.message || "unknown error";
                  dispatch(showToast({ message: `Ext ${ext.extensionNumber}: ${msg}`, variant: "warning" }));
                });
            }, 4000);
            return;
          }
          const backendMsg = err?.response?.data?.message || err?.message || "unknown error";
          dispatch(showToast({
            message: `Ext ${ext.extensionNumber}: ${backendMsg}`,
            variant: "warning",
          }));
        });
    }), 2000);
  };

  // Retry SDK connection for an extension (red/yellow dot).
  // extNum=null or matching activeExtensionRef → re-triggers the main init useEffect.
  const retryExtensionInit = async (extNum) => {
    // Destroy and evict any broken/stale instance from the pool
    const toClean = extNum ? [extNum] : Object.keys(sdkPoolRef.current);
    toClean.forEach((ext) => {
      const inst = sdkPoolRef.current[ext];
      if (inst) {
        try { inst.destroy?.(); } catch {}
        delete sdkPoolRef.current[ext];
      }
      delete initInProgressRef.current[ext];
      setExtStatus(ext, "connecting");
    });

    const isPrimary = !extNum || extNum === activeExtensionRef.current;
    if (isPrimary) {
      setIsInitialized(false);
      setPhoneStatus("disconnected");
      dispatch(resetYeastar());
      setInitRetryKey((k) => k + 1);
      return;
    }

    // Secondary extension — re-init directly
    try {
      await initExtensionById(extNum);
      dispatch(showToast({ message: `Ext ${extNum} reconnected`, variant: "success" }));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Retry failed";
      dispatch(showToast({ message: `Ext ${extNum}: ${msg}`, variant: "danger" }));
    }
  };

  useEffect(() => {
    const initPhone = async () => {
      if (yeastar.phone) {
        setActivePhone(yeastar.phone, yeastar.pbx, yeastar.destroy);
        const primaryExt = yeastar.username || userProfile?.extensionNumber;
        if (primaryExt && !sdkPoolRef.current[primaryExt]) {
          sdkPoolRef.current[primaryExt] = { phone: yeastar.phone, pbx: yeastar.pbx, destroy: yeastar.destroy };
        }
        activeExtensionRef.current = primaryExt;
        if (primaryExt) {
          setExtStatus(primaryExt, "ready");
          everConnectedRef.current.add(String(primaryExt));
        }
        setIsInitialized(true);
        setPhoneStatus(yeastar.status || "ready");
        dispatch(fetchProfile());
        // Agents warm-start all their own assigned extensions (no filtering needed).
        // Admins skip numbers already delegated to agents.
        if (userProfile?.role === "user") {
          initSecondaryExtensions(primaryExt);
        } else {
          api.get("/admin/user/getAgent")
            .then((r) => {
              const agentNums = new Set((r.data?.data || []).flatMap((a) => a.assignedCallerNumbers || []));
              initSecondaryExtensions(primaryExt, agentNums);
            })
            .catch(() => initSecondaryExtensions(primaryExt));
        }
        return;
      }

      try {
        const hasAnyExtension =
          userProfile?.extensionNumber ||
          userProfile?.PBXDetails?.PBX_EXTENSION_NUMBER ||
          userProfile?.assignedExtensions?.length;
        if (!hasAnyExtension) {
          setError("Extension not configured. Please contact support.");
          setPhoneStatus("disconnected");
          return;
        }

        let credentials;
        if (yeastar.signature && yeastar.pbxURL && yeastar.username) {
          credentials = { username: yeastar.username, signature: yeastar.signature, pbxURL: yeastar.pbxURL };
        } else {
          // Try primary first; on failure try each assigned extension in order.
          const fallbackExts = (userProfile?.assignedExtensions || []).map((e) => e.extensionNumber).filter(Boolean);
          let lastErr;
          for (const extNum of [null, ...fallbackExts]) {
            try {
              credentials = await dispatch(fetchYeastarSignature(extNum)).unwrap();
              break;
            } catch (e) {
              lastErr = e;
            }
          }
          if (!credentials) throw lastErr;
        }

        const result = await dispatch(
          initializeYeastarSDK({
            username: credentials.username,
            signature: credentials.signature,
            pbxURL: credentials.pbxURL,
          })
        ).unwrap();

        setActivePhone(result.phone, result.pbx, result.destroy);
        activeExtensionRef.current = credentials.username;
        sdkPoolRef.current[credentials.username] = result;
        setExtStatus(credentials.username, "ready");
        everConnectedRef.current.add(String(credentials.username));
        setIsInitialized(true);
        setPhoneStatus("ready");
        setError(null);
        dispatch(fetchProfile());

        // Warm-start secondary extensions in background.
        // Agents: warm-start all their own assigned extensions.
        // Admins: skip numbers already delegated to agents.
        if (userProfile?.role === "user") {
          initSecondaryExtensions(credentials.username);
        } else {
          api.get("/admin/user/getAgent")
            .then((r) => {
              const agentNums = new Set((r.data?.data || []).flatMap((a) => a.assignedCallerNumbers || []));
              initSecondaryExtensions(credentials.username, agentNums);
            })
            .catch(() => initSecondaryExtensions(credentials.username));
        }
      } catch (err) {
        setError(err || "Failed to initialize phone");
        setPhoneStatus("disconnected");
      }
    };

    initPhone();
  }, [userProfile?.extensionNumber, yeastar.phone, dispatch, initRetryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Switch active extension before a call using the pre-initialized pool ────
  // overrideCaller allows the makeCall effect to pass floatingDialer.callerNumber
  // directly (avoiding stale selectedCallerNumber local state).
  const ensureExtension = async (overrideCaller = null) => {
    const callerToUse = overrideCaller || selectedCallerNumber;
    const selectedEntry = callerNumbers.find((n) => n.number === callerToUse);

    if (!selectedEntry?.isExtension || !selectedEntry.extensionNumber) {
      return true;
    }

    const targetExt = selectedEntry.extensionNumber;

    // ── Device-conflict eviction ──────────────────────────────────────────────
    // If another pool entry shares the same assignedDeviceId as targetExt, evict
    // it now. Co-registering two extensions on one device makes the PBX attribute
    // all calls to whichever extension registered first.
    // Skip for multichannel cloud extensions — PBX handles simultaneous registrations
    // correctly and eviction would cause unnecessary re-registration delay.
    const targetExtEntry = (userProfile?.assignedExtensions || []).find((e) => e.extensionNumber === targetExt);
    const targetIsMultichannel = (parseInt(targetExtEntry?.channels, 10) || 1) > 1;
    const targetDeviceId = targetExtEntry?.assignedDeviceId?.toString() ?? null;

    let isDeviceConflictSwitch = false;
    if (targetDeviceId && !targetIsMultichannel) {
      let hadConflict = false;
      Object.keys(sdkPoolRef.current).forEach((ext) => {
        if (ext === targetExt) return;
        const extDeviceId = (userProfile?.assignedExtensions || [])
          .find((e) => e.extensionNumber === ext)
          ?.assignedDeviceId?.toString() ?? null;
        if (extDeviceId && extDeviceId === targetDeviceId) {
          const conflictInst = sdkPoolRef.current[ext];
          console.log(`[Call] evicting ext ${ext} (shares device ${targetDeviceId} with target ext ${targetExt})`);
          try { conflictInst?.destroy?.(); } catch {}
          delete sdkPoolRef.current[ext];
          delete initInProgressRef.current[ext];
          evictedExtensionsRef.current.push(ext);
          hadConflict = true;
        }
      });
      if (hadConflict) {
        isDeviceConflictSwitch = true;
        // Only evict targetExt if it is NOT already registered — if it is registered,
        // keep it in the pool and place the call immediately. The conflicting ext's
        // deregister is irrelevant since the SIP INVITE authenticates as targetExt.
        const targetInst = sdkPoolRef.current[targetExt];
        if (targetInst && !targetInst.phone?.isRegistered) {
          try { targetInst.destroy?.(); } catch {}
          delete sdkPoolRef.current[targetExt];
          delete initInProgressRef.current[targetExt];
        }
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    // Only skip switch when BOTH the activeExtensionRef AND phoneRef.current match the target pool
    // entry. activeExtensionRef alone can be stale — the initPhone effect resets it to primary
    // (e.g. 1010) without updating phoneRef, so a false "already on ext" skip would leave
    // phoneRef.current pointing to whatever phone was last active.
    const poolPhoneForTarget = sdkPoolRef.current[targetExt]?.phone;
    const alreadyOnTarget =
      targetExt === activeExtensionRef.current &&
      poolPhoneForTarget != null &&
      phoneRef.current === poolPhoneForTarget;
    if (alreadyOnTarget) {
      return true;
    }

    let instance = sdkPoolRef.current[targetExt];
    if (!instance?.phone) {
      const assigned = (userProfile?.assignedExtensions || []).find(
        (e) => e.extensionNumber === targetExt && e.enabled !== false
      );
      if (!assigned) {
        dispatch(showToast({ message: `Extension ${targetExt} is not in your calling pool`, variant: "danger" }));
        return false;
      }
      const reconnecting = everConnectedRef.current.has(String(targetExt));
      try {
        if (!isDeviceConflictSwitch && !reconnecting) {
          dispatch(showToast({ message: `Connecting extension ${targetExt}…`, variant: "info" }));
        }
        instance = await initExtensionById(targetExt, isDeviceConflictSwitch || reconnecting);
        if (!instance?.phone) throw new Error("Init returned no phone");
      } catch (err) {
        dispatch(showToast({ message: `Failed to connect extension ${targetExt}: ${err.message}`, variant: "danger" }));
        return false;
      }
    } else {
      // If the warm-started instance isn't registered yet, wait up to 15 s.
      // If an init is still in progress (warm-start promise), await it directly.
      // Otherwise wait on the phone events themselves.
      if (instance.phone && !instance.phone.isRegistered) {
        if (!isDeviceConflictSwitch && !everConnectedRef.current.has(String(targetExt))) {
          dispatch(showToast({ message: `Connecting extension ${targetExt}…`, variant: "info" }));
        }
        const waitResult = await new Promise((resolve) => {
          // Re-check — background init may have completed between the isRegistered
          // check above and now; if so, the 'registered' event already fired and we'd
          // wait 15 s for nothing.
          if (instance.phone.isRegistered) { resolve(true); return; }
          // If the warm-start promise is still running, await it directly
          if (initInProgressRef.current[targetExt]) { 
            initInProgressRef.current[targetExt]
              .then(() => resolve(true))
              .catch(() => resolve(false));
            return;
          }
          // Otherwise wait on the phone events
          const timer = setTimeout(() => {
            instance.phone.removeListener("registered", onOk);
            instance.phone.removeListener("registrationFailed", onFail);
            resolve(false);
          }, 15000);
          const onOk = () => { clearTimeout(timer); instance.phone.removeListener("registrationFailed", onFail); resolve(true); };
          const onFail = () => { clearTimeout(timer); instance.phone.removeListener("registered", onOk); resolve(false); };
          instance.phone.once("registered", onOk);
          instance.phone.once("registrationFailed", onFail);
        });
        if (!waitResult) {
          dispatch(showToast({ message: `Extension ${targetExt} failed to connect`, variant: "danger" }));
          return false;
        }
      }
    }

    setActivePhone(instance.phone, instance.pbx, instance.destroy);
    activeExtensionRef.current = targetExt;
    return true;
  };

  // ─── Auto-dial when a number is pushed from Redux ──────────────────────────
  // useEffect(() => {
  //   async function makeCall() {
  //     if (floatingDialer.phoneNumber && isInitialized && phoneRef.current) {
  //       try {
  //         // Save full original number (with country code) for the API in handleStartSession
  //         originalDialledNumberRef.current = floatingDialer.phoneNumber;

  //         // Convert to local format (0XXXXXXXXX) for the PBX call only
  //         const localNumber = toLocalDialFormat(floatingDialer.phoneNumber);

  //         dispatch(
  //           showToast({
  //             message: `Calling ${floatingDialer.phoneNumber}...`,
  //             variant: "info",
  //           })
  //         );

  //         await phoneRef.current.call(localNumber);

  //         dispatch(openDialer());
  //         dispatch(clearNumber());
  //       } catch (err) {
  //         console.error("❌ Failed to make call:", err);

  //         if (err.code === -202) {
  //           dispatch(
  //             showToast({
  //               message:
  //                 "Microphone access denied. Please allow microphone permission in your browser and reload the page.",
  //               variant: "danger",
  //             })
  //           );
  //         } else {
  //           dispatch(
  //             showToast({ message: "Failed to make call", variant: "danger" })
  //           );
  //         }
  //         dispatch(closeDialer());
  //       }
  //     }
  //   }
  //   makeCall();
  // }, [floatingDialer.phoneNumber, isInitialized, dispatch]);

  useEffect(() => {
  async function makeCall() {
    if (floatingDialer.phoneNumber) {
      if (!isInitialized || !phoneRef.current) {
        dispatch(showToast({
          message: "Phone is not connected. Please wait for the dialer to connect or contact your administrator.",
          variant: "danger",
        }));
        dispatch(clearNumber());
        return;
      }

      const micOk = await checkMicrophoneAvailable();
      if (!micOk) {
        dispatch(showToast({
          message: "No microphone found or access denied. Please check your mic settings.",
          variant: "danger",
        }));
        dispatch(clearNumber());
        return;
      }
      // Save full original number
      originalDialledNumberRef.current = floatingDialer.phoneNumber;

      // Use floatingDialer.callerNumber first (freshly set by setCallerAndNumber),
      // fall back to local selectedCallerNumber for manual-dialer calls.
      const effectiveCaller = floatingDialer.callerNumber || selectedCallerNumber;

      // Convert to local format
      const localNumber = toLocalDialFormat(floatingDialer.phoneNumber, effectiveCaller);

      dispatch(showToast({ message: `Calling ${floatingDialer.phoneNumber}...`, variant: "info" }));

      console.log(`[Call] from="${effectiveCaller || "(none)"}" → to="${floatingDialer.phoneNumber}" | activeExt=${activeExtensionRef.current}`);
      try {
        const extSwitchOk = await ensureExtension(effectiveCaller);
        if (!extSwitchOk) { dispatch(clearNumber()); return; }

        console.log(`[Call] SIP dial | ext=${activeExtensionRef.current} | local="${localNumber}"`);
        await phoneRef.current.call(localNumber);
        dispatch(openDialer());
        dispatch(clearNumber());
      } catch (err) {
        if (err.code === -202) {
          dispatch(showToast({
            message: "Microphone access denied. Please allow microphone permission in your browser and reload the page.",
            variant: "danger",
          }));
        } else {
          dispatch(showToast({ message: "Failed to make call", variant: "danger" }));
        }
        dispatch(closeDialer());
      }
    }
  }

  makeCall();
}, [floatingDialer.phoneNumber, floatingDialer.callerNumber, isInitialized, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Phone event listeners ──────────────────────────────────────────────────
  // useEffect(() => {
  //   const phone = phoneRef.current;
  //   if (!phone) return;

  //   const handleStartSession = async ({ callId, session }) => {
  //     setCurrentSession(session);
  //     setCallStatus(session.status.callStatus);
  //     setCallInfo(session.status);

  //     /**
  //      * session.status.number is the LOCAL format (e.g. "0501234567") because
  //      * that's what we passed to pbx.call(). For the API we need the full number
  //      * with country code, which we saved in originalDialledNumberRef before calling.
  //      *
  //      * For MANUAL dial (extension numbers), there is no country code — send as-is
  //      * with empty countryCode so the API handles it.
  //      */
  //     const savedNumber = originalDialledNumberRef.current;
  //     console.log("handleStartSession — saved original number:", savedNumber);


  //     const digitsOnly = (savedNumber || session.status.number || "").replace(/\D/g, "");
  //     console.log(digitsOnly, "digitsOnly");

  //     if (digitsOnly.length <= 4) {
  //       skipOffcanvasRef.current = true; // mark to skip offcanvas
  //       // ...
  //       return;
  //     }
  //     skipOffcanvasRef.current = false;




  //     // ✅ Skip API + offcanvas for short numbers (extensions < 4 digits)
  //     if (digitsOnly.length < 4) {
  //       setCallPhoneNumber(savedNumber || session.status.number);
  //       originalDialledNumberRef.current = null;
  //       return; // skip API call
  //     }



  //     let payload;

  //     if (savedNumber) {
  //       const isManual = !savedNumber.includes("+") && !savedNumber.startsWith("971");
  //       if (isManual) {
  //         // Extension / manual dial — no country code
  //         payload = {
  //           phoneNumbers: {
  //             countryCode: "",
  //             number: savedNumber,
  //           },
  //         };
  //       } else {
  //         payload = toApiPayload(savedNumber);
  //       }
  //     } else {
  //       // Fallback: use session number (will be local format, best effort)
  //       payload = toApiPayload(session.status.number);
  //     }

  //     console.log("API payload:", payload);

  //     const response = await api.post("/call/find-by-phone-number", payload);
  //     const fname = response.data.data.firstname || "";
  //     const lname = response.data.data.lastname || "";
  //     setFirstName(fname);
  //     setLastName(lname);

  //     // Update recent dial entry with the resolved contact name
  //     if (savedNumber && (fname || lname)) {
  //       saveRecentDial(savedNumber, `${fname} ${lname}`.trim());
  //     }

  //     // Display the original number if available, else session number
  //     setCallPhoneNumber(savedNumber || session.status.number);

  //     // Clear after use
  //     originalDialledNumberRef.current = null;
  //   };

  //   const handleDeleteSession = async ({ callId, cause }) => {
  //     console.log(cause, "failure msg");
  //     stopRingtone();
  //     closeCallNotification();
  //     setIncomingSession(null);
  //     setCurrentSession(null);
  //     setCallStatus("");
  //     setCallDuration(0);
  //     setCallInfo(null);
  //     setPhoneStatus("ready");
  //     setIsMuted(false);
  //     setIsOnHold(false);
  //     setShowDialpad(false);
  //     console.log(skipOffcanvasRef.current, "skipOffcanvasRef.current");


  //     if (skipOffcanvasRef.current) {
  //       skipOffcanvasRef.current = false;
  //       return;
  //     }

  //     const offcanvasEl = document.getElementById("afterCall_offcanvas");
  //     const isOffcanvasOpen = offcanvasEl?.classList.contains("show");
  //     if (isOffcanvasOpen) return;

  //     if (
  //       cause === "Unavailable" ||
  //       cause === "Not Found" ||
  //       cause === "Failed"
  //     ) {
  //       return;
  //     }
  //     offcanvasTriggerRef.current?.click();
  //   };


  //   const handleIncoming = async ({ callId, session }) => {

  //     // NOTE: ringtone audio is NOT played here — handled by the incomingSession
  //     // useEffect after the component re-renders with the <audio> element mounted.

  //     api.get("/call/incoming-call-webhook"); // Notify backend
  //     dispatch(openDialer());
  //     setIncomingSession(session);

  //     // Show a browser notification so the agent is alerted even if the tab
  //     // is in the background. Requires notification permission (granted once).
  //     showCallNotification(session);

  //     const rawNumber = session.status.number;
  //     console.log("handleIncoming raw number:", rawNumber);


  //     const digitsOnly = rawNumber.replace(/\D/g, "");
  //     if (digitsOnly.length < 4) {
  //       skipOffcanvasRef.current = true; // mark to skip offcanvas
  //       // ...
  //       return;
  //     }
  //     skipOffcanvasRef.current = false;



  //     // ✅ Skip API + offcanvas for short numbers
  //     if (digitsOnly.length < 4) {
  //       setCallPhoneNumber(rawNumber);
  //       setPhoneStatus("ringing");
  //       return; // skip API call
  //     }



  //     let nationalNumber;
  //     let countryCode;
  //     let displayNumber;

  //     if (rawNumber.startsWith("0")) {
  //       // Local UAE number
  //       countryCode = "971";
  //       nationalNumber = rawNumber.slice(1); // remove leading 0
  //       displayNumber = `+${countryCode}${nationalNumber}`;
  //     } else {
  //       // International number — parse using libphonenumber-js
  //       const phoneNumber = parsePhoneNumberFromString(rawNumber);
  //       if (phoneNumber && phoneNumber.isValid()) {
  //         countryCode = phoneNumber.countryCallingCode;   // e.g., "971"
  //         nationalNumber = phoneNumber.nationalNumber;    // e.g., "501234567"
  //         displayNumber = phoneNumber.number;             // e.g., "+971501234567"
  //       } else {
  //         // Fallback: treat as raw number
  //         countryCode = null;
  //         nationalNumber = rawNumber.replace(/^\+/, "");
  //         displayNumber = rawNumber.startsWith("+") ? rawNumber : `+${rawNumber}`;
  //         console.warn("Unable to parse incoming number:", rawNumber);
  //       }
  //     }

  //     // Prepare API payload
  //     const payload = {
  //       phoneNumbers: {
  //         number: nationalNumber, // number WITHOUT country code
  //         countryCode,                 // country code separately
  //       }
  //     };
  //     console.log("API payloadss:", payload);

  //     try {
  //       const response = await api.post("/call/find-by-phone-number", payload);
  //       console.log(response.data, "hgjfdghjkl");

  //       // Set caller info
  //       setFirstName(response.data.data.firstname);
  //       setLastName(response.data.data.lastname);
  //       setPhoneStatus("ringing");
  //     } catch (err) {
  //       console.error("Error fetching phone details:", err);
  //     }

  //     // Display normalized number in UI
  //     setCallPhoneNumber(displayNumber);
  //   };




  //   phone.on("startSession", handleStartSession);
  //   phone.on("deleteSession", handleDeleteSession);
  //   phone.on("incoming", handleIncoming);

  //   return () => {
  //     phone.removeListener("startSession", handleStartSession);
  //     phone.removeListener("deleteSession", handleDeleteSession);
  //     phone.removeListener("incoming", handleIncoming);
  //   };
  // }, [yeastar.phone, dispatch]);
  const handleStartSession = async ({ callId, session }) => {
  console.log("[DEBUG] handleStartSession called | callId=", callId, "| session=", session);
  currentCallIdRef.current = callId; // store for live billing
  const savedNumber = originalDialledNumberRef.current;
  const displayNumber = savedNumber || session.status.number || "";

  setCurrentSession(session);
  setCallStatus(session.status.callStatus);
  setCallInfo(session.status);
  // Set immediately so AfterCallOffcanvas always has the number,
  // even if the contact-lookup API call below hasn't finished yet.
  setCallPhoneNumber(displayNumber);

  const digitsOnly = displayNumber.replace(/\D/g, "");

  if (digitsOnly.length <= 4) {
    skipOffcanvasRef.current = true;
    return;
  }

  skipOffcanvasRef.current = false;

  if (digitsOnly.length < 4) {
    setCallPhoneNumber(displayNumber);
    originalDialledNumberRef.current = null;
    return;
  }

  let payload;
  if (savedNumber) {
    const isManual = !savedNumber.includes("+") && !savedNumber.startsWith("971");
    payload = isManual
      ? { phoneNumbers: { countryCode: "", number: savedNumber } }
      : toApiPayload(savedNumber);
  } else {
    payload = toApiPayload(session.status.number);
  }

  try {
    const response = await api.post("/call/find-by-phone-number", payload);
    const record = response.data.data || {};
    const fname = record.firstname || "";
    const lname = record.lastname || "";

    setFirstName(fname);
    setLastName(lname);
    if (record._id) setFoundContactRecord(record);

    if (savedNumber) {
      const callerNum = selectedCallerNumberRef.current;
      const callerEntry = callerNumbersRef.current.find((n) => n.number === callerNum);
      const callerLabel = callerEntry?.extensionNumber || callerNum || null;
      saveRecentDial(savedNumber, (fname || lname) ? `${fname} ${lname}`.trim() : "", callerLabel);
    }

    setCallPhoneNumber(displayNumber);
    originalDialledNumberRef.current = null;
  } catch (err) {
    // Contact lookup failed — still save the recent dial entry
    if (savedNumber) {
      const callerNum = selectedCallerNumberRef.current;
      const callerEntry = callerNumbersRef.current.find((n) => n.number === callerNum);
      const callerLabel = callerEntry?.extensionNumber || callerNum || null;
      saveRecentDial(savedNumber, "", callerLabel);
    }
  }
};

  // ─── PBX event listeners (connection-level) ────────────────────────────────
  useEffect(() => {
    const pbx = pbxRef.current;
    if (!pbx) return;

    const handleRuntimeError = async (reason) => {
      const { message } = reason || {};
      setError(`Connection error: ${message}`);
      setPhoneStatus("disconnected");

      try {
        if (destroyRef.current) {
          destroyRef.current();
        }
        const phone = await dispatch(
          initializeYeastarSDK(
            userProfile.extensionNumber,
            userProfile.yeastarSignature,
            userProfile.pbxURL
          )
        ).unwrap();
        setActivePhone(phone, pbxRef.current, destroyRef.current);
        setPhoneStatus("ready");
      } catch (err) {
      }
    };

    pbx.on("runtimeError", handleRuntimeError);

    let count = 0;
    const onConnectedChangeHandler = () => {
      if (count > 0) {
        // Suppress for multichannel extensions — a co-registrant connecting or
        // reconnecting causes a disconnect/reconnect on the shared SIP line.
        const hasMultichannel = (userProfile?.assignedExtensions || []).some(
          (e) => (e.channels || 1) > 1
        );
        if (!hasMultichannel) {
          dispatch(showLoggedInElsewhereModal());
        }
      }
      count++;
    };

    if (pbx?.socket?.onConnectedChange) {
      pbx.socket.onConnectedChange(onConnectedChangeHandler);
    }

    return () => {
      pbx?.removeListener("runtimeError", handleRuntimeError);
    };
  }, [phoneKey, dispatch, userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Incoming call listener — attached to primary AND all pool extensions ─────
  // Incoming SIP INVITEs can arrive on any registered extension, so we attach the
  // same handler to every phone instance in sdkPoolRef as well as yeastar.phone.
  useEffect(() => {
    const phone = yeastar.phone;
    if (!phone) return;

    // Keep fresh copy for factory calls outside this effect (initExtensionById)
    assignedExtensionsRef.current = userProfile?.assignedExtensions || [];

    // Factory: creates a handler that records which extension received the call
    const makeHandler = (extNum, extTel) => (eventArg) => {
      try {
        const session = eventArg?.session ?? eventArg;
        if (!session) return;
        api.get("/call/incoming-call-webhook").catch(() => {});
        dispatch(openDialer());
        setIncomingSession(session);
        setPhoneStatus("ringing");
        if (extNum) setIncomingExtension({ extensionNumber: extNum, PBX_TELEPHONE: extTel || null });
        const number = session?.status?.number || "";
        if (number) setCallPhoneNumber(number);
        showCallNotification(session);

        // Auto-dismiss when caller hangs up before we answer
        const dismiss = () => {
          setIncomingSession((p) => (p === session ? null : p));
          setIncomingExtension(null);
          setPhoneStatus((p) => (p === "ringing" ? "ready" : p));
          stopRingtone();
          closeCallNotification();
        };
        try { session.once("ended", dismiss); } catch { try { session.on("ended", dismiss); } catch {} }
        try { session.once("failed", dismiss); } catch { try { session.on("failed", dismiss); } catch {} }
      } catch (err) {}
    };

    // Store factory so initExtensionById can create handlers for new extensions
    makeIncomingHandlerRef.current = makeHandler;

    // Primary phone handler
    const primaryExt = (userProfile?.assignedExtensions || [])[0];
    const primaryHandler = makeHandler(primaryExt?.extensionNumber || null, primaryExt?.PBX_TELEPHONE || null);
    extHandlersRef.current["__primary__"] = primaryHandler;
    incomingHandlerRef.current = primaryHandler; // backward compat
    phone.on("incoming", primaryHandler);

    // Per-extension handlers for already-initialized pool phones
    Object.entries(sdkPoolRef.current).forEach(([extNum, inst]) => {
      if (!inst?.phone || inst.phone === phone) return;
      const prev = extHandlersRef.current[extNum];
      if (prev) inst.phone.removeListener("incoming", prev);
      const extEntry = (userProfile?.assignedExtensions || []).find((e) => e.extensionNumber === extNum);
      const h = makeHandler(extNum, extEntry?.PBX_TELEPHONE || null);
      extHandlersRef.current[extNum] = h;
      inst.phone.on("incoming", h);
    });

    return () => {
      phone?.removeListener("incoming", extHandlersRef.current["__primary__"] || primaryHandler);
      Object.entries(sdkPoolRef.current).forEach(([extNum, inst]) => {
        const h = extHandlersRef.current[extNum];
        if (inst?.phone && inst.phone !== phone && h) inst.phone.removeListener("incoming", h);
      });
      incomingHandlerRef.current = null;
      makeIncomingHandlerRef.current = null;
      extHandlersRef.current = {};
    };
  }, [yeastar.phone, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Phone event listeners (startSession / deleteSession) ────────────────────
  // These must follow phoneRef.current so they track whichever extension is
  // active for the current call (primary or switched secondary).
  useEffect(() => {
    const phone = phoneRef.current;
    if (!phone) return;

    const handleDeleteSession = ({ callId, cause }) => {
      stopRingtone();
      closeCallNotification();
      setCurrentSession((prev) => (prev?.status?.callId === callId ? null : prev));
      setIncomingSession(null);
      setCallStatus("");
      setCallDuration(0);
      setCallInfo(null);
      setFoundContactRecord(null);
      setPhoneStatus("ready");
      setIsMuted(false);
      setIsOnHold(false);
      setShowDialpad(false);

      // Re-register any extensions evicted before this call so incoming calls
      // work on all extensions again. Skip during an active Power Dialer session —
      // re-registering a conflicting extension only to evict it again on the next
      // auto-dial causes a race where Yeastar may still see both registered when
      // the SIP INVITE is sent, attributing the call to the wrong extension.
      const toRestore = evictedExtensionsRef.current.splice(0);
      if (toRestore.length > 0 && !powerDialerActiveRef.current) {
        setTimeout(() => {
          toRestore.forEach((extNum) => {
            if (!sdkPoolRef.current[extNum] && !initInProgressRef.current[extNum]) {
              initExtensionById(extNum, true).catch(() => {});
            }
          });
        }, 1000);
      }

      // Skip for short numbers / extension calls (set in handleStartSession)
      if (skipOffcanvasRef.current) {
        skipOffcanvasRef.current = false;
        return;
      }

      // Skip for technical failures where no meaningful call occurred
      if (cause === "Unavailable" || cause === "Not Found" || cause === "Failed") {
        return;
      }

      const offcanvasEl = document.getElementById("afterCall_offcanvas");
      if (offcanvasEl?.classList.contains("show")) return;

      offcanvasTriggerRef.current?.click();
    };

    console.log("[DEBUG] Attaching startSession/deleteSession listeners | phoneKey=", phoneKey, "| phone=", phone);
    const wrappedStartSession = (...args) => {
      console.log("[DEBUG] startSession event fired (raw) | args=", args);
      handleStartSession(...args);
    };
    phone.on("startSession", wrappedStartSession);
    phone.on("deleteSession", handleDeleteSession);

    return () => {
      phone?.removeListener("startSession", wrappedStartSession);
      phone?.removeListener("deleteSession", handleDeleteSession);
    };
  }, [phoneKey, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Current session event listeners ───────────────────────────────────────
  useEffect(() => {
    if (!currentSession) return;

    const session = currentSession;

    const handleConfirmed = ({ callId, session }) => {
      setCallStatus(session.status.callStatus);
      setPhoneStatus("calling");
    };
    const handleAccepted = ({ callId: id, session: s }) => {
      setCallStatus(s.status.callStatus);
      setPhoneStatus("talking");

      // ── Live billing: only for outbound calls to external numbers with a DID ──
      if (isOutboundCallRef.current && liveDestinationRef.current) {
        const destination = liveDestinationRef.current;
        const sdkCallId   = currentCallIdRef.current;

        const deduct = async () => {
          try {
            const res = await api.post("/billing/live-minute", {
              destination,
              sdkCallId,
              selectedDIDNumber: billingCallerNumber,
            });
            if (res.data.success) {
              const newBal  = res.data.newBalance;
              const thr     = res.data.threshold ?? liveBillingThresholdRef.current ?? 5;
              setLiveBalance(newBal);
              dispatch(setReduxBalance(newBal));

              // Low balance check
              if (newBal < thr) {
                if (autoRechargeEnabledRef.current) {
                  // Auto-recharge is ON — attempt it
                  try {
                    const arRes = await api.post("/billing/trigger-auto-recharge");
                    if (arRes.data.triggered && arRes.data.newBalance > thr) {
                      setLiveBalance(arRes.data.newBalance);
                      setLowBalanceWarning(false);
                      dispatch(setReduxBalance(arRes.data.newBalance));
                      dispatch(showToast({ message: `Auto-recharged — new balance: $${Number(arRes.data.newBalance).toFixed(2)}`, variant: "success" }));
                    } else {
                      // Auto-recharge ran but balance still low
                      setLowBalanceWarning(true);
                    }
                  } catch {
                    setLowBalanceWarning(true);
                  }
                } else {
                  // Auto-recharge is OFF — just show the warning
                  setLowBalanceWarning(true);
                }
              } else {
                setLowBalanceWarning(false);
              }
            } else if (res.data.reason === "insufficient_balance") {
              // Balance ran out — try auto-recharge only if it's enabled
              if (autoRechargeEnabledRef.current) {
                try {
                  const arRes = await api.post("/billing/trigger-auto-recharge");
                  if (arRes.data.triggered && arRes.data.newBalance > 0) {
                    setLiveBalance(arRes.data.newBalance);
                    dispatch(setReduxBalance(arRes.data.newBalance));
                    dispatch(showToast({ message: `Auto-recharged — call continuing`, variant: "success" }));
                    return; // don't disconnect — next interval tick will retry deduction
                  }
                } catch { /* fall through to disconnect */ }
              }
              // Auto-recharge off or failed — disconnect
              clearInterval(liveBillingIntervalRef.current);
              liveBillingIntervalRef.current = null;
              session.terminate("hangup");
              dispatch(showToast({ message: "Call disconnected — balance too low to continue", variant: "danger" }));
            } else if (res.data.reason === "no_did") {
              // No purchased DID — no billing needed, stop interval silently
              clearInterval(liveBillingIntervalRef.current);
              liveBillingIntervalRef.current = null;
            }
          } catch (err) {
          }
        };

        setLiveBillingActive(true);
        deduct();  // first second — deduct immediately
        liveBillingIntervalRef.current = setInterval(deduct, 60000);
      }
    };
    const handleFailed = ({ callId, cause }) => {
      // Call never connected — no billing interval would have started
      isOutboundCallRef.current  = false;
      liveDestinationRef.current = null;
      currentCallIdRef.current   = null;
      setCurrentSession(null);
      setIncomingSession(null);
      setCallStatus("");
      setCallDuration(0);
      setCallInfo(null);
      setFoundContactRecord(null);
      setPhoneStatus("ready");
      setIsMuted(false);
      setIsOnHold(false);
      setShowDialpad(false);
      const label = cause === "Rejected"       ? "Call rejected by the other party"
                  : cause === "486 Busy Here"  ? "Line is busy"
                  : cause === "Answered elsewhere" ? "Answered elsewhere"
                  : cause || "Call failed";
      dispatch(showToast({ message: label, variant: "danger" }));
    };
    const handleEnded = ({ callId, cause }) => {
      // Stop live billing interval and notify backend
      if (liveBillingIntervalRef.current) {
        clearInterval(liveBillingIntervalRef.current);
        liveBillingIntervalRef.current = null;
      }
      const endedSdkCallId = currentCallIdRef.current;
      if (endedSdkCallId) {
        api.post("/billing/call-ended", { sdkCallId: endedSdkCallId }).catch(() => {});
      }
      setLiveBillingActive(false);
      setLowBalanceWarning(false);
      setLiveBalance(null);
      isOutboundCallRef.current   = false;
      liveDestinationRef.current  = null;
      currentCallIdRef.current    = null;

      setCurrentSession(null);
      setCallStatus("");
      setPhoneStatus("ready");
      setCallDuration(0);
      setCallInfo(null);
      setFoundContactRecord(null);
      setIsMuted(false);
      setIsOnHold(false);
      setShowDialpad(false);
    };
    const handleUpdateTimer = ({ callId, timer }) => {
      if (timer.callDuration !== undefined) setCallDuration(timer.callDuration);
    };
    const handleStatusChange = (newStatus) => {
      setCallInfo(newStatus);
      setCallStatus(newStatus.callStatus);
      if (incomingSession && newStatus.callStatus === "terminated") {
        setIncomingSession(null);
        setPhoneStatus("ready");
      }
    };
    const handleUpdateRemoteStream = ({ remoteStream }) => {
      if (remoteStream?.getTracks?.() && remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
      }
    };

    session.on("confirmed", handleConfirmed);
    session.on("accepted", handleAccepted);
    session.on("failed", handleFailed);
    session.on("ended", handleEnded);
    session.on("updateTimer", handleUpdateTimer);
    session.on("statusChange", handleStatusChange);
    session.on("updateRemoteStream", handleUpdateRemoteStream);

    return () => {
      session.removeListener("confirmed", handleConfirmed);
      session.removeListener("accepted", handleAccepted);
      session.removeListener("failed", handleFailed);
      session.removeListener("ended", handleEnded);
      session.removeListener("updateTimer", handleUpdateTimer);
      session.removeListener("statusChange", handleStatusChange);
      session.removeListener("updateRemoteStream", handleUpdateRemoteStream);
      // Ensure interval is cleared if the component re-renders while on a call
      if (liveBillingIntervalRef.current) {
        clearInterval(liveBillingIntervalRef.current);
        liveBillingIntervalRef.current = null;
      }
    };
  }, [currentSession, dispatch]);

  // ─── Dragging ───────────────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: Math.min(Math.max(e.clientX - dragOffset.x, 0), window.innerWidth - 340),
      y: Math.min(Math.max(e.clientY - dragOffset.y, 0), window.innerHeight - 100),
    });
  };
  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, position]);

  // Keep fromNumberRef in sync so the auto-dial timeout reads the latest value
  useEffect(() => {
    fromNumberRef.current = powerDialer.fromNumber;
  }, [powerDialer.fromNumber]);

  // ─── Power Dialer: watch for next number to auto-dial ──────────────────────
  useEffect(() => {
    if (!powerDialer.nextDialNumber || !powerDialer.isActive) return;
    const number = powerDialer.nextDialNumber;
    const t = setTimeout(() => {
      // Read ref at fire time — not closure value — so campaign edits during the
      // 1500ms window use the updated number, not the stale captured value.
      const fromNum = fromNumberRef.current;
      dispatch(clearPowerDialerNextNumber());
      dispatch(setCallerAndNumber({ callerNumber: fromNum || null, phoneNumber: number }));
    }, 1500);
    return () => clearTimeout(t);
  }, [powerDialer.nextDialNumber, powerDialer.isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Power Dialer: restore active session on mount ─────────────────────────
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const res = await api.get("/api/power-dialer/sessions/active");
        if (res.data.data) {
          const { session, contact } = res.data.data;
          const fromNumber = session?.campaign_id?.from_number || null;
          dispatch(setPowerDialerSession({ session, contact, fromNumber }));
        }
      } catch {}
    };
    restoreSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Call controls ──────────────────────────────────────────────────────────
  const handleMuteToggle = () => {
    if (currentSession) {
      if (isMuted) currentSession.unmute();
      else currentSession.mute();
      setIsMuted(!isMuted);
    }
  };

  const handleHoldToggle = () => {
    if (currentSession) {
      if (isOnHold) currentSession.unhold();
      else currentSession.hold();
      setIsOnHold(!isOnHold);
    }
  };

  const handleHangup = () => currentSession?.terminate("hangup");

  const handleCloseDialer = () => {
    try { if (currentSession) currentSession.terminate("hangup"); } catch {}
    try { if (incomingSession) incomingSession.reject(); } catch {}
    stopRingtone();
    closeCallNotification();
    setIncomingSession(null);
    setIncomingExtension(null);
    dispatch(closeDialer());
  };

  const handleAnswerIncoming = async () => {
    const micOk = await checkMicrophoneAvailable();
    if (!micOk) {
      dispatch(showToast({
        message: "No microphone found or access denied. Cannot answer the call.",
        variant: "danger",
      }));
      try { incomingSession?.reject(); } catch {}
      stopRingtone();
      closeCallNotification();
      setIncomingSession(null);
      setIncomingExtension(null);
      return;
    }
    incomingSession?.answer();
    stopRingtone();
    closeCallNotification();
    setIncomingSession(null);
    setIncomingExtension(null);
  };

  const handleRejectIncoming = () => {
    try { incomingSession?.reject(); } catch (err) {
    }
    stopRingtone();
    closeCallNotification();
    setIncomingSession(null);
    setIncomingExtension(null);
    setPhoneStatus("ready");
  };

  // ─── Dialpad call ───────────────────────────────────────────────────────────
  const handleDialpadCall = async () => {
    if (!isInitialized || !phoneRef.current) {
      dispatch(showToast({
        message: "Phone is not connected. Please wait for the dialer to connect or contact your administrator.",
        variant: "danger",
      }));
      return;
    }
    if (!isExtensionEnabled) {
      dispatch(
        showToast({
          message: "Extension is disabled. Kindly contact administrator",
          variant: "danger",
        })
      );
      return;
    }
    flushSync(() => setIsDialpadCalling(true));
    try {
      if (isManualDial) {
        // Manual (extension) dial — short numbers are internal, never billed
        if (!manualNumber) {
          dispatch(showToast({ message: "Please enter a phone number", variant: "warning" }));
          return;
        }
        originalDialledNumberRef.current = manualNumber;
        isOutboundCallRef.current        = true;
        await phoneRef?.current?.call(toLocalDialFormat(manualNumber));
        setManualNumber("");
      } else {
        // PhoneInput dial
        if (!phoneNumber || phoneNumber === "+971") {
          dispatch(showToast({ message: "Please enter a phone number", variant: "warning" }));
          return;
        }
        const fullNumber = phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
        const digits = fullNumber.replace(/\D/g, "");

        // Pre-call balance check (skip for short extension numbers)
        if (digits.length > 6) {
          try {
            const checkRes = await api.post("/billing/pre-call-check", {
              destination: fullNumber,
              selectedDIDNumber: billingCallerNumber,
            });
            if (!checkRes.data.canCall) {
              dispatch(showToast({
                message: `Insufficient balance — need $${checkRes.data.ratePerMin?.toFixed(4) ?? "?"}/min, have $${Number(checkRes.data.currentBalance ?? 0).toFixed(2)}`,
                variant: "danger",
              }));
              return;
            }
            if (checkRes.data.threshold !== undefined) liveBillingThresholdRef.current = checkRes.data.threshold;
            autoRechargeEnabledRef.current = !!checkRes.data.autoRechargeEnabled;
          } catch (err) {
          }
        }

        originalDialledNumberRef.current = fullNumber;
        liveDestinationRef.current       = fullNumber;
        isOutboundCallRef.current        = true;
        saveRecentDial(fullNumber);
        const localNumber = toLocalDialFormat(phoneNumber, selectedCallerNumber);
        console.log(`[Call] manual dial | from="${selectedCallerNumber || "(none)"}" → to="${fullNumber}" | activeExt=${activeExtensionRef.current}`);
        if (!await ensureExtension()) return;
        console.log(`[Call] SIP dial | ext=${activeExtensionRef.current} | local="${localNumber}" | phone=`, phoneRef?.current);
        const callResult = await phoneRef?.current?.call(localNumber);
        console.log("[DEBUG] call() resolved | result=", callResult, "| currentSession state will update via startSession event");
        const callerDigits = (selectedCallerNumber || "").replace(/\D/g, "");
        setPhoneNumber(callerDigits.startsWith("0") ? "" : "+971");
      }
    } finally {
      setIsDialpadCalling(false);
    }
  };

  // ─── DTMF / dialpad input ───────────────────────────────────────────────────
  const handleSendDTMF = (digit) => {
    if (currentSession && showDialpad) {
      setDialpadInput((prev) => prev + digit);
      try {
        if (typeof currentSession.sendDTMF === "function") {
          currentSession.sendDTMF(digit);
        } else if (typeof currentSession.dtmf === "function") {
          currentSession.dtmf(digit);
        } else if (currentSession.session && typeof currentSession.session.sendDTMF === "function") {
          currentSession.session.sendDTMF(digit);
        } else {
          dispatch(showToast({ message: "DTMF not supported in this call", variant: "warning" }));
        }
      } catch (err) {
        dispatch(showToast({ message: "Failed to send DTMF tone", variant: "danger" }));
      }
    } else if (!currentSession) {
      if (isManualDial) {
        // setManualNumber((prev) => prev + digit);
        setManualNumber(prev => {
          if (prev.length >= 4) return prev;
          return prev + digit;
        });
      } else {
        setPhoneNumber((prev) => prev + digit);
      }
    }
  };

  const handleDialpadClear = () => {
    if (currentSession && showDialpad) {
      setDialpadInput((prev) => (prev.length <= 1 ? "" : prev.slice(0, -1)));
    } else {
      if (isManualDial) {
        setManualNumber((prev) => prev.slice(0, -1));
      } else {
        setPhoneNumber((prev) => {
          const callerDigits = (selectedCallerNumber || "").replace(/\D/g, "");
          const empty = callerDigits.startsWith("0") ? "" : "+971";
          return prev.length <= 1 ? empty : prev.slice(0, -1);
        });
      }
    }
  };

  // ─── Status helpers ─────────────────────────────────────────────────────────
  const getStatusColor = () => {
    switch (phoneStatus) {
      case "ready": return "bg-green-500";
      case "calling":
      case "ringing":
      case "talking": return "bg-yellow-500";
      case "disconnected": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ─── Visibility ─────────────────────────────────────────────────────────────
  const isVisible = floatingDialer.isOpen || !!incomingSession;

  const showIncomingScreen = !!incomingSession;
  const showActiveCallScreen = !!currentSession && !incomingSession;
  const showDialerScreen = !currentSession && !incomingSession;

  // ─── Render ─────────────────────────────────────────────────────────────────
  // NOTE: The ringtone <audio> is rendered unconditionally (outside the isVisible
  // gate) so ringtoneRef.current is always available for the autoplay-unlock
  // listener and the incomingSession useEffect.
  return (
    <>
    {/* Ringtone fallback — HTMLAudioElement used if AudioContext is still blocked */}
    <audio ref={ringtoneRef} src="/assets/sounds/ringtone.mp3" preload="auto" style={{ display: "none" }} />

    {/* ── Notification permission banner ─────────────────────────────────────── */}
    {notifPermission === "default" && !bannerDismissed && (
      <div
        style={{
          position: "fixed", bottom: "24px", left: "50%",
          transform: "translateX(-50%)", zIndex: 9999,
          background: "#1e293b", color: "#fff", borderRadius: "12px",
          padding: "12px 20px", display: "flex", alignItems: "center",
          gap: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
          fontSize: "14px", whiteSpace: "nowrap",
        }}
      >
        <span>🔔</span>
        <span
          onClick={requestNotifPermission}
          style={{ cursor: "pointer" }}
        >
          Click to enable incoming call alerts
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); setBannerDismissed(true); }}
          style={{ cursor: "pointer", color: "#94a3b8", fontSize: "18px", lineHeight: 1, marginLeft: 4 }}
          title="Dismiss"
        >
          ✕
        </span>
      </div>
    )}

    {/* ── Notification blocked warning ─────────────────────────────────────────
        Clicking the text re-triggers requestPermission — Chrome will show
        the permission dialog again if the user opens it from settings first,
        otherwise it's a no-op (the browser prevents re-prompting when denied). */}
    {notifPermission === "denied" && !blockedDismissed && (
      <div
        style={{
          position: "fixed", bottom: "24px", left: "50%",
          transform: "translateX(-50%)", zIndex: 9999,
          background: "#7f1d1d", color: "#fca5a5", borderRadius: "12px",
          padding: "10px 18px", display: "flex", alignItems: "center",
          gap: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
          fontSize: "13px", whiteSpace: "nowrap",
        }}
      >
        <span>
          ⚠️ Notifications blocked — enable in browser settings to hear incoming calls
        </span>
        <span
          onClick={() => setBlockedDismissed(true)}
          style={{ cursor: "pointer", color: "#f87171", fontSize: "18px", lineHeight: 1, marginLeft: 4 }}
          title="Dismiss"
        >
          ✕
        </span>
      </div>
    )}
    {/* ── Mobile FAB — always visible on mobile when dialer is closed ── */}
    {isMobile && !floatingDialer.isOpen && (
      <button
        className="mobile-dialer-fab"
        onClick={() => dispatch(openDialer())}
        aria-label={currentSession || incomingSession ? "Return to call" : "Open dialer"}
        style={
          incomingSession
            ? { background: "#ef4444" }
            : currentSession
            ? { background: "#10b981" }
            : {}
        }
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z" />
        </svg>
        {(currentSession || incomingSession) && (
          <span className={`fab-badge ${incomingSession ? "incoming" : "active"}`} />
        )}
      </button>
    )}

    {isVisible && <div
      className={`floating-dialer ${isDarkMode ? "dark" : "light"}`}
      style={isMobile ? {
        position: "fixed",
        top: 70,
        left: 0,
        bottom: "unset",
        right: 0,
        width: "100%",
        maxWidth: "340px",
        margin: "0 auto",
        maxHeight: "90vh",
        overflow: "hidden auto",
        borderRadius: "0 0 14px 14px",
        boxShadow: "0 4px 32px rgba(0,0,0,0.4)",
        zIndex: 1000,
        userSelect: "none",
        cursor: "default",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      } : {
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: "340px",
        borderRadius: "20px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        overflow: "hidden",
        zIndex: 1000,
        userSelect: "none",
        cursor: isDragging ? "grabbing" : "default",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <button
        ref={offcanvasTriggerRef}
        type="button"
        data-bs-toggle="offcanvas"
        data-bs-target="#afterCall_offcanvas"
        style={{ display: "none" }}
      />

      {/* Header */}
      <div
        style={{
          background: "rgba(255,255,255,0.05)",
          padding: "16px 20px",
          cursor: isMobile ? "default" : "grab",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
        onMouseDown={isMobile ? undefined : handleMouseDown}
      >
        <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          {showIncomingScreen || showActiveCallScreen ? (
            <>
              <div style={{ width: "10px", height: "10px", borderRadius: "50%", flexShrink: 0 }} className={getStatusColor()} />
              <h6 style={{ color: "var(--dialer-text)", margin: 0, fontWeight: 600, fontSize: "16px" }}>
                {showIncomingScreen ? "Incoming Call" : "Active Call"}
              </h6>
            </>
          ) : !callerNumbersLoaded ? (
            /* ── Loading: shimmer skeleton while APIs resolve ── */
            <div style={{ height: 28, width: 160, borderRadius: 8, background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ) : callerNumbers.length > 0 ? (
            /* ── Compact "Calling From" selector in header ── */
            <div ref={callerDropdownRef} style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
              {(() => {
                const sel       = callerNumbers.find((n) => n.number === selectedCallerNumber);
                const isDefault = userProfile?.defaultCallerNumber === selectedCallerNumber;
                const extStatus = sel?.isExtension
                  ? (extStatuses[sel.extensionNumber] || "connecting")
                  : (phoneStatus === "ready" ? "ready" : phoneStatus === "disconnected" ? "failed" : "connecting");
                const dotColor  = extStatus === "ready" ? "#22c55e" : extStatus === "failed" ? "#ef4444" : "#f59e0b";
                return (
                  <>
                    <button
                      ref={callerTriggerRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        if (callerTriggerRef.current) {
                          const r = callerTriggerRef.current.getBoundingClientRect();
                          setCallerDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
                        }
                        setCallerDropdownOpen((o) => !o);
                      }}
                      style={{
                        flex: 1, minWidth: 0, overflow: "hidden", display: "flex", alignItems: "center", gap: "6px",
                        padding: "5px 10px", borderRadius: "8px",
                        border: "1.5px solid var(--dialer-border)",
                        background: "var(--dialer-button-bg)", cursor: "pointer",
                        color: "var(--dialer-text)", fontSize: "12px", fontWeight: 500,
                      }}
                    >
                      <span style={{ color: "var(--dialer-subtext)", fontSize: "9px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.05em", textTransform: "uppercase" }}>From</span>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                      <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {sel ? getCallerLabel(sel) : "Select number"}
                      </span>
                      {isDefault && <span style={{ fontSize: "11px", color: "#fbbf24", flexShrink: 0 }}>★</span>}
                      <span style={{ fontSize: "9px", color: "var(--dialer-subtext)", flexShrink: 0 }}>▾</span>
                    </button>
                  </>
                );
              })()}
              {/* Dropdown list rendered via portal — see end of return */}
            </div>
          ) : (
            /* ── No extension assigned ── */
            <div style={{
              flex: 1, padding: "4px 10px", borderRadius: 8,
              border: "1.5px solid var(--dialer-border)",
              background: "var(--dialer-button-bg)",
              display: "flex", alignItems: "center", gap: 6,
              marginRight: 8,
            }}>
              <span style={{ fontSize: 13 }}>⚠️</span>
              <span style={{ fontSize: 11, color: "var(--dialer-subtext)", lineHeight: 1.3 }}>
                No extension assigned. Contact admin.
              </span>
            </div>
          )}
        </div>
        <div className="d-flex">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            style={{
              background: "var(--dialer-button-bg)",
              border: "none",
              color: "#fff",
              fontSize: "16px",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              cursor: "pointer",
              marginRight: "8px",
            }}
          >
            {isDarkMode ? "☀️" : "🌙"}
          </button>
          {showActiveCallScreen && (
            <button
              style={{
                background: "var(--dialer-button-bg)",
                border: "none",
                color: "#fff",
                fontSize: "20px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 10,
              }}
              data-bs-toggle="offcanvas"
              data-bs-target="#afterCall_offcanvas"
            >
              ✍️
            </button>
          )}
          <button
            style={{
              background: "var(--dialer-button-bg)",
              border: "none",
              color: "#fff",
              fontSize: "20px",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={currentSession || incomingSession ? "Minimize" : "Close"}
            onClick={currentSession || incomingSession ? () => dispatch(closeDialer()) : handleCloseDialer}
          >
            {currentSession || incomingSession ? "−" : "×"}
          </button>
        </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "10px 24px 24px 24px" }}>

        {/* ── INCOMING CALL ── */}
        {showIncomingScreen && (
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "100px", height: "100px",
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                borderRadius: "50%",
                margin: "0 auto 24px",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "48px", color: "#fff", fontWeight: "bold",
                boxShadow: "0 10px 30px rgba(59,130,246,0.4)",
              }}
            >
              {incomingSession.status.name?.[0] || "?"}
            </div>
            <div style={{ color: "var(--dialer-text)", fontSize: "24px", fontWeight: 600, marginBottom: "8px" }}>
              {incomingSession.status.name || "Unknown Caller"}
            </div>
            <div style={{ color: "var(--dialer-subtext)", fontSize: "16px", marginBottom: "8px" }}>
              {incomingSession.status.number}
            </div>
            {incomingExtension && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  background: "rgba(59,130,246,0.12)",
                  border: "1px solid rgba(59,130,246,0.3)",
                  borderRadius: "20px", padding: "4px 12px",
                }}>
                  <span style={{
                    background: "#3b82f6", color: "#fff",
                    borderRadius: "10px", padding: "1px 7px",
                    fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em",
                  }}>
                    Ext {incomingExtension.extensionNumber}
                  </span>
                  {incomingExtension.PBX_TELEPHONE && (
                    <span style={{ color: "var(--dialer-subtext)", fontSize: "12px", fontFamily: "monospace" }}>
                      {incomingExtension.PBX_TELEPHONE}
                    </span>
                  )}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "16px", justifyContent: "center" }}>
              <button
                onClick={handleRejectIncoming}
                style={{
                  width: "64px", height: "64px",
                  background: "#ef4444", border: "none", borderRadius: "50%",
                  color: "var(--dialer-text)", fontSize: "28px", cursor: "pointer",
                  boxShadow: "0 8px 20px rgba(239,68,68,0.4)", transition: "transform 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.transform = "scale(1.1)")}
                onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
              >✖</button>
              <button
                onClick={handleAnswerIncoming}
                style={{
                  width: "64px", height: "64px",
                  background: "#10b981", border: "none", borderRadius: "50%",
                  color: "var(--dialer-text)", fontSize: "28px", cursor: "pointer",
                  boxShadow: "0 8px 20px rgba(16,185,129,0.4)", transition: "transform 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.transform = "scale(1.1)")}
                onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
              >📞</button>
            </div>
          </div>
        )}

        {/* ── ACTIVE CALL ── */}
        {showActiveCallScreen && (
          <div style={{ textAlign: "center" }}>
            {!showDialpad ? (
              <>
                <div
                  style={{
                    width: "100px", height: "100px",
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    borderRadius: "50%",
                    margin: "0 auto 24px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "48px", color: "#fff", fontWeight: "bold",
                    boxShadow: "0 10px 30px rgba(16,185,129,0.4)",
                  }}
                >
                  {callInfo?.name?.[0] || callInfo?.number?.[0] || "?"}
                </div>
                <div style={{ color: "var(--dialer-text)", fontSize: "24px", fontWeight: 600, marginBottom: "8px" }}>
                  {((callInfo?.name || callInfo?.number) ?? "").replace(/%20/g, " ").trim() || "Unknown"}
                </div>
                <div style={{ color: "var(--dialer-subtext)", fontSize: "16px", marginBottom: "4px" }}>
                  {formatTime(callDuration)}
                </div>
                {selectedCallerNumber && (
                  <div style={{ color: "var(--dialer-subtext)", fontSize: "12px", marginBottom: "8px", opacity: 0.75 }}>
                    From: {getCallerLabel(callerNumbers.find((n) => n.number === selectedCallerNumber) || { number: selectedCallerNumber, isExtension: false })}
                  </div>
                )}

                {/* ── Live balance + low-balance warning ── */}
                {liveBillingActive && liveBalance !== null && (
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 6, marginBottom: 8,
                    padding: "4px 12px", borderRadius: 10,
                    background: lowBalanceWarning ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.1)",
                  }}>
                    {lowBalanceWarning && <span style={{ fontSize: 14 }}>⚠️</span>}
                    <span style={{ fontSize: 12, fontWeight: 700, color: lowBalanceWarning ? "#ef4444" : "#10b981" }}>
                      Balance: ${Number(liveBalance).toFixed(2)}
                    </span>
                    {lowBalanceWarning && (
                      <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 600 }}>Low balance!</span>
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: "inline-block", padding: "4px 12px",
                    background: "rgba(16,185,129,0.2)", borderRadius: "12px",
                    color: "#10b981", fontSize: "12px", fontWeight: 600, marginBottom: foundContactRecord?._id ? "12px" : "32px",
                  }}
                >
                  {callStatus}
                </div>

                {foundContactRecord?._id && (
                  <div style={{ marginBottom: "20px" }}>
                    <button
                      onClick={() => {
                        const r = foundContactRecord;
                        const transformed = {
                          _originalData: r,
                          id: r._id,
                          key: r._id,
                          customer_name: `${r.firstname || ""} ${r.lastname || ""}`.trim(),
                          email: r.emailAddresses?.[0] || "",
                          allEmails: r.emailAddresses || [],
                          phone: r.phoneNumbers?.[0]
                            ? `${r.phoneNumbers[0].countryCode || ""} ${r.phoneNumbers[0].number || ""}`.trim()
                            : "",
                          allPhones: (r.phoneNumbers || []).map((p) =>
                            `${p.countryCode || ""} ${p.number || ""}`.trim()
                          ),
                          customer_company: r.company || "",
                          customer_occupation: r.designation || "",
                          customer_image: r.contactImageURL || "",
                          isFavourite: r.isFavourite || false,
                          status: r.status || "",
                          website: r.website || "",
                          notes: r.notes || "",
                          isLead: r.isLead || false,
                        };
                        const isAgent = userProfile?.role === "user";
                        navigate(
                          r.isLead
                            ? (isAgent ? all_routes.agentLeadsDetails : all_routes.leadsDetails)
                            : (isAgent ? all_routes.agentContactDetails : all_routes.contactDetails),
                          { state: { record: transformed } }
                        );
                      }}
                      style={{
                        background: "rgba(99,102,241,0.15)",
                        border: "1px solid rgba(99,102,241,0.3)",
                        borderRadius: "8px",
                        color: "#818cf8",
                        fontSize: "12px",
                        fontWeight: 600,
                        padding: "5px 14px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <i className="ti ti-external-link" style={{ fontSize: 13 }} />
                      View {foundContactRecord.isLead ? "Lead" : "Contact"}
                    </button>
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}>
                  <button
                    onClick={handleMuteToggle}
                    style={{
                      background: "var(--dialer-button-bg)",
                      border: isMuted ? "2px solid #ef4444" : "none",
                      padding: "16px", borderRadius: "12px",
                      color: "var(--dialer-text)", cursor: "pointer",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", fontSize: "24px",
                    }}
                  >
                    <span>{isMuted ? "🔇" : "🔊"}</span>
                    <span style={{ fontSize: "12px" }}>{isMuted ? "Unmute" : "Mute"}</span>
                  </button>
                  <button
                    onClick={handleHoldToggle}
                    style={{
                      background: "var(--dialer-button-bg)",
                      border: isOnHold ? "2px solid #fbbf24" : "none",
                      padding: "16px", borderRadius: "12px",
                      color: "var(--dialer-text)", cursor: "pointer",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", fontSize: "24px",
                    }}
                  >
                    <span>⏸</span>
                    <span style={{ fontSize: "12px" }}>{isOnHold ? "Resume" : "Hold"}</span>
                  </button>
                  <button
                    onClick={() => setShowDialpad(true)}
                    style={{
                      background: "var(--dialer-button-bg)", border: "none",
                      padding: "16px", borderRadius: "12px",
                      color: "var(--dialer-text)", cursor: "pointer",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", fontSize: "24px",
                    }}
                  >
                    <span>🔢</span>
                    <span style={{ fontSize: "12px" }}>Dialpad</span>
                  </button>
                </div>

                <button
                  onClick={handleHangup}
                  style={{
                    width: "64px", height: "64px",
                    background: "#ef4444", border: "none", borderRadius: "50%",
                    color: "var(--dialer-text)", fontSize: "28px", cursor: "pointer",
                    boxShadow: "0 8px 20px rgba(239,68,68,0.4)", margin: "0 auto", display: "block",
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z" />
                  </svg>

                </button>
              </>
            ) : (
              <>
                <div style={{ color: "var(--dialer-text)", fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>
                  {((callInfo?.name || callInfo?.number) ?? "").replace(/%20/g, " ").trim() || "Unknown"}
                </div>
                <div style={{ color: "var(--dialer-subtext)", fontSize: "14px", marginBottom: "16px" }}>
                  {formatTime(callDuration)}
                </div>
                <div
                  style={{
                    color: "var(--dialer-subtext)", fontSize: "16px", textAlign: "center",
                    padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px",
                    marginBottom: "16px", minHeight: "44px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {dialpadInput || "Enter IVR number"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", width: "100%", justifyContent: "space-between" }}>
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((num) => (
                    <button
                      key={num}
                      onClick={() => handleSendDTMF(num)}
                      style={{
                        padding: "16px", background: "var(--dialer-button-bg)",
                        border: "1px solid rgba(94,94,94,0.15)",
                        borderRadius: "50%", width: "70px", height: "70px",
                        color: "var(--dialer-text)", fontSize: "20px", fontWeight: "600", cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => { e.target.style.background = "rgba(255,255,255,0.2)"; e.target.style.transform = "scale(1.05)"; }}
                      onMouseLeave={(e) => { e.target.style.background = "rgba(255,255,255,0.1)"; e.target.style.transform = "scale(1)"; }}
                    >{num}</button>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", marginTop: "10px" }}>
                  <button
                    onClick={() => setShowDialpad(false)}
                    style={{
                      flex: 1, padding: "12px", background: "var(--dialer-button-bg)",
                      border: "none", borderRadius: "12px", color: "var(--dialer-text)", fontSize: "14px", cursor: "pointer",
                    }}
                  >← Back</button>
                  <button
                    onClick={handleHangup}
                    style={{
                      width: "56px", height: "56px", background: "#ef4444",
                      border: "none", borderRadius: "50%", color: "var(--dialer-text)",
                      fontSize: "24px", cursor: "pointer", boxShadow: "0 8px 20px rgba(239,68,68,0.4)",
                    }}
                  >📞</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── DIALER SCREEN ── */}
        {showDialerScreen && (
          <div className="floating-dialer">

            {/* Tab toggle: Dialpad | Recents */}
            <div style={{
              display: "flex", gap: 4, marginBottom: 14, borderRadius: 10, padding: 4,
              background: isDarkMode ? "rgba(255,255,255,0.06)" : "rgb(248, 249, 250)",
              border: isDarkMode ? "none" : "1px solid #e2e8f0",
            }}>
              {["Dialpad", "Recents"].map((tab) => {
                const active = tab === "Dialpad" ? !showRecents : showRecents;
                return (
                  <button
                    key={tab}
                    onClick={() => setShowRecents(tab === "Recents")}
                    style={{
                      flex: 1, padding: "7px 0", border: "none", borderRadius: 7, cursor: "pointer",
                      fontWeight: 600, fontSize: 13, transition: "all 0.2s",
                      background: active ? "#d1d5db" : "transparent",
                      color: active ? "#fff" : "var(--dialer-subtext)",
                      boxShadow: active ? "0 2px 8px rgba(99,102,241,0.35)" : "none",
                    }}
                  >
                    {tab === "Recents" && recentDials.length > 0 && !active && (
                      <span style={{
                        display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                        background: "#fbbf24", marginRight: 5, verticalAlign: "middle",
                      }} />
                    )}
                    {tab}
                  </button>
                );
              })}
            </div>

            {/* ── RECENT CALLS LIST ── */}
            {showRecents ? (
              <div style={{ position: "relative" }}>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

                {/* ── Top bar: search + refresh ── */}
                <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center" }}>
                  <div style={{ flex: 1, position: "relative" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5"
                      style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search number, agent…"
                      value={recentSearch}
                      onChange={(e) => setRecentSearch(e.target.value)}
                      style={{
                        width: "100%", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 8, padding: "6px 8px 6px 26px", color: "var(--dialer-text)",
                        fontSize: 12, outline: "none",
                      }}
                    />
                  </div>
                  <button
                    onClick={handleRefreshRecents}
                    disabled={recentRefreshing || recentCallsLoading}
                    title="Sync calls from server"
                    style={{
                      display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
                      background: "rgba(99,102,241,0.15)", border: "none", borderRadius: 8,
                      padding: "6px 10px", cursor: "pointer", color: "#818cf8", fontSize: 12, fontWeight: 600,
                      opacity: (recentRefreshing || recentCallsLoading) ? 0.6 : 1,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2.5"
                      style={{ animation: (recentRefreshing || recentCallsLoading) ? "spin 1s linear infinite" : "none" }}>
                      <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                    {recentRefreshing ? "Syncing…" : "Sync"}
                  </button>
                </div>

                {/* ── Temporary dials (localStorage — before sync) ── */}
                {recentDials.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                      <span style={{ color: "#f59e0b", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        ⏳ Just Dialled
                      </span>
                      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                    </div>
                    <div style={{ maxHeight: 160, overflowY: "auto", paddingRight: 2 }}>
                      {recentDials.map((r, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 9,
                        padding: "8px 10px", borderRadius: 10, marginBottom: 4,
                        background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.15)",
                      }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                          background: "rgba(245,158,11,0.15)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {/* Outgoing arrow */}
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="17 7 7 17"/><polyline points="7 7 17 7 17 17"/>
                          </svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <button
                            onClick={() => openNumberHistory(r.number, r.name)}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
                          >
                            <div style={{ color: "var(--dialer-text)", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {r.name || r.number}
                            </div>
                            {r.name && <div style={{ color: "#94a3b8", fontSize: 11 }}>{r.number}</div>}
                            {r.callerNumber && <div style={{ color: "#64748b", fontSize: 10 }}>via {r.callerNumber}</div>}
                          </button>
                        </div>
                        <button
                          onClick={() => dialRecentNumber(r.number)}
                          title={`Call ${r.number}`}
                          disabled={!!dialingNumber}
                          style={{
                            height: 26, borderRadius: dialingNumber === r.number ? 13 : "50%", border: "none",
                            width: dialingNumber === r.number ? "auto" : 26,
                            padding: dialingNumber === r.number ? "0 8px" : 0,
                            background: dialingNumber === r.number ? "#10b981" : "rgba(16,185,129,0.15)", cursor: dialingNumber ? "default" : "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            fontSize: 10, fontWeight: 600, color: "white", whiteSpace: "nowrap",
                          }}
                          onMouseEnter={(e) => { if (!dialingNumber) { e.currentTarget.style.background = "#10b981"; const svg = e.currentTarget.querySelector("svg"); if (svg) { svg.style.fill = "white"; svg.style.stroke = "white"; } } }}
                          onMouseLeave={(e) => { if (!dialingNumber) { e.currentTarget.style.background = "rgba(16,185,129,0.15)"; const svg = e.currentTarget.querySelector("svg"); if (svg) { svg.style.fill = "#10b981"; svg.style.stroke = ""; } } }}
                        >
                          {dialingNumber === r.number
                            ? "Dialing..."
                            : <svg width="11" height="11" viewBox="0 0 24 24" fill="#10b981"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z"/></svg>
                          }
                        </button>
                      </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── API call records ── */}
                {recentCallsLoading ? (
                  <div style={{ textAlign: "center", padding: "28px 0", color: "var(--dialer-subtext)", fontSize: 13 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2"
                      style={{ animation: "spin 1s linear infinite", display: "block", margin: "0 auto 8px" }}>
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/>
                    </svg>
                    Loading…
                  </div>
                ) : recentCallsData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px 0", color: "var(--dialer-subtext)", fontSize: 12 }}>
                    <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>📞</div>
                    {recentSearch ? "No calls match your search." : "No call history. Hit Sync to load."}
                  </div>
                ) : (
                  <div style={{ overflowY: "auto", maxHeight: 310, paddingRight: 2 }}>
                    {(() => {
                      const grouped = {};
                      recentCallsData.forEach((r, i) => {
                        const day = dayjs(r.start_time).format("YYYY-MM-DD");
                        if (!grouped[day]) grouped[day] = [];
                        grouped[day].push({ ...r, _key: i });
                      });
                      const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

                      return sortedDates.map((dateStr) => (
                        <div key={dateStr}>
                          {/* Date separator */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0 5px" }}>
                            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                            <span style={{ color: "#64748b", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                              {getDateLabel(dateStr)}
                            </span>
                            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                          </div>

                          {grouped[dateStr].map((r) => {
                            const { isInbound, isMissed, number, answered, viaNumber } = getCallMeta(r);
                            const duration = formatCallDuration(r.duration);
                            const isPlaying = recentPlayingKey === r._key;
                            const isLoadingAudio = recentAudioLoading === r._key;
                            const iconColor = isMissed ? "#ef4444" : isInbound ? "#10b981" : "#6366f1";

                            return (
                              <div key={r._key}>
                                <div style={{
                                  display: "flex", alignItems: "center", gap: 8,
                                  padding: "8px 10px", borderRadius: 10, marginBottom: 4,
                                  background: "rgba(255,255,255,0.04)",
                                }}>
                                  {/* Direction badge */}
                                  <div style={{
                                    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                                    background: `${iconColor}18`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                  }}>
                                    {isMissed ? (
                                      /* Missed: phone with x mark */
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2"/>
                                        <line x1="17" y1="1" x2="23" y2="7"/><line x1="23" y1="1" x2="17" y2="7"/>
                                      </svg>
                                    ) : isInbound ? (
                                      /* Inbound: phone with arrow pointing inward (down-left) */
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2"/>
                                        <polyline points="17 8 13 8 13 12"/>
                                      </svg>
                                    ) : (
                                      /* Outbound: phone + diagonal arrow up-right */
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2"/>
                                        <line x1="14" y1="9" x2="20" y2="3"/>
                                        <polyline points="14 3 20 3 20 9"/>
                                      </svg>
                                    )}
                                  </div>

                                  {/* Info — click number to show detail modal */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <button
                                      onClick={() => !number || number === "—" ? null : openNumberHistory(number, r.ownerName)}
                                      disabled={!number || number === "—"}
                                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", width: "100%" }}
                                    >
                                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span style={{
                                          color: isMissed ? "#f87171" : "var(--dialer-text)",
                                          fontSize: 12, fontWeight: 600,
                                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                          textDecoration: "underline", textDecorationStyle: "dotted", textDecorationColor: "rgba(255,255,255,0.2)",
                                        }}>
                                          {number}
                                        </span>
                                        {r.ownerName && <span style={{ color: "#64748b", fontSize: 10, whiteSpace: "nowrap", flexShrink: 0 }}>· {r.ownerName}</span>}
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
                                        <span style={{ color: "#64748b", fontSize: 10 }}>{dayjs(r.start_time).format("h:mm A")}</span>
                                        {duration && <span style={{ color: "#64748b", fontSize: 10 }}>· {duration}</span>}
                                        <span style={{ fontSize: 10, color: iconColor, fontWeight: 700 }}>· {isMissed ? "Missed" : isInbound ? "In" : "Out"}</span>
                                        {viaNumber && viaNumber !== number && <span style={{ color: "#64748b", fontSize: 10 }}>· via {viaNumber}</span>}
                                      </div>
                                    </button>
                                  </div>

                                  {/* Action buttons */}
                                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                    {answered && r.record_file && (
                                      <button
                                        onClick={() => handlePlayRecentRecording(r.record_file, r._key)}
                                        title={isPlaying ? "Stop" : "Play recording"}
                                        style={{
                                          width: 26, height: 26, borderRadius: "50%", border: "none",
                                          background: isPlaying ? "rgba(99,102,241,0.4)" : "rgba(99,102,241,0.15)",
                                          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                        }}
                                      >
                                        {isLoadingAudio
                                          ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2.5" style={{ animation: "spin 1s linear infinite" }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
                                          : isPlaying
                                            ? <svg width="10" height="10" viewBox="0 0 24 24" fill="#818cf8"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                            : <svg width="10" height="10" viewBox="0 0 24 24" fill="#818cf8"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                        }
                                      </button>
                                    )}
                                    <button
                                      onClick={() => dialRecentNumber(number)}
                                      title={`Call back ${number}`}
                                      disabled={!!dialingNumber}
                                      style={{
                                        height: 26, borderRadius: dialingNumber === number ? 13 : "50%", border: "none",
                                        width: dialingNumber === number ? "auto" : 26,
                                        padding: dialingNumber === number ? "0 8px" : 0,
                                        background: dialingNumber === number ? "#10b981" : "rgba(16,185,129,0.15)", cursor: dialingNumber ? "default" : "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 10, fontWeight: 600, color: "white", whiteSpace: "nowrap",
                                      }}
                                      onMouseEnter={(e) => { if (!dialingNumber) { e.currentTarget.style.background = "#10b981"; const svg = e.currentTarget.querySelector("svg"); if (svg) { svg.style.fill = "white"; svg.style.stroke = "white"; } } }}
                                      onMouseLeave={(e) => { if (!dialingNumber) { e.currentTarget.style.background = "rgba(16,185,129,0.15)"; const svg = e.currentTarget.querySelector("svg"); if (svg) { svg.style.fill = "#10b981"; svg.style.stroke = ""; } } }}
                                    >
                                      {dialingNumber === number
                                        ? "Dialing..."
                                        : <svg width="10" height="10" viewBox="0 0 24 24" fill="#10b981"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z"/></svg>
                                      }
                                    </button>
                                  </div>
                                </div>

                                {/* Inline audio player */}
                                {isPlaying && recentAudioUrl && (
                                  <div style={{ margin: "0 0 5px 40px", padding: "5px 8px", background: "rgba(99,102,241,0.1)", borderRadius: 7, borderLeft: "2px solid #6366f1" }}>
                                    <audio key={recentAudioUrl} controls autoPlay style={{ width: "100%", height: 28, accentColor: "#6366f1" }}
                                      onEnded={() => { setRecentAudioUrl(null); setRecentPlayingKey(null); }}>
                                      <source src={recentAudioUrl} />
                                    </audio>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </div>
                )}

                {/* ── Number Call History Panel ── */}
                {selectedCallDetail && (
                  <div style={{
                    position: "absolute", inset: 0, zIndex: 50,
                    background: "var(--dialer-bg, #0f111a)", borderRadius: 18,
                    display: "flex", flexDirection: "column", overflow: "hidden",
                  }}>
                    {/* Header */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "14px 14px 10px",
                      borderBottom: "1px solid rgba(255,255,255,0.07)",
                      flexShrink: 0,
                    }}>
                      <button
                        onClick={() => setSelectedCallDetail(null)}
                        style={{
                          background: "rgba(255,255,255,0.08)", border: "none", borderRadius: "50%",
                          width: 28, height: 28, cursor: "pointer", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
                          <polyline points="15 18 9 12 15 6"/>
                        </svg>
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {selectedCallDetail.name && (
                          <div style={{ color: "var(--dialer-text)", fontWeight: 700, fontSize: 13, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {selectedCallDetail.name}
                          </div>
                        )}
                        <div style={{ color: "#94a3b8", fontSize: 11 }}>{selectedCallDetail.number}</div>
                      </div>
                      <button
                        onClick={() => { setSelectedCallDetail(null); dialRecentNumber(selectedCallDetail.number); }}
                        title="Call back"
                        disabled={!!dialingNumber}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          background: dialingNumber === selectedCallDetail?.number ? "#10b981" : "rgba(16,185,129,0.15)", border: "none", borderRadius: 20,
                          padding: "6px 12px", cursor: dialingNumber ? "default" : "pointer", color: "#10b981", fontSize: 12, fontWeight: 600, flexShrink: 0,
                        }}
                        onMouseEnter={(e) => { if (!dialingNumber) { e.currentTarget.style.background = "#10b981"; e.currentTarget.style.color = "white"; const svg = e.currentTarget.querySelector("svg"); if (svg) { svg.style.fill = "white"; svg.style.stroke = "white"; } } }}
                        onMouseLeave={(e) => { if (!dialingNumber) { e.currentTarget.style.background = "rgba(16,185,129,0.15)"; e.currentTarget.style.color = "#10b981"; const svg = e.currentTarget.querySelector("svg"); if (svg) { svg.style.fill = "#10b981"; svg.style.stroke = ""; } } }}
                      >
                        {!dialingNumber || dialingNumber !== selectedCallDetail?.number
                          ? <svg width="12" height="12" viewBox="0 0 24 24" fill="#10b981"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z"/></svg>
                          : null
                        }
                        {dialingNumber === selectedCallDetail?.number ? "Dialing..." : "Call"}
                      </button>
                    </div>

                    {/* Call log list */}
                    <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
                      {numHistoryLoading ? (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--dialer-subtext)", fontSize: 13 }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2"
                            style={{ animation: "spin 1s linear infinite", display: "block", margin: "0 auto 8px" }}>
                            <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/>
                          </svg>
                          Loading…
                        </div>
                      ) : numHistoryData.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--dialer-subtext)", fontSize: 12 }}>
                          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>📞</div>
                          No call history found for this number.
                        </div>
                      ) : numHistoryData.map((r, i) => {
                        const dir = r.direction?.toLowerCase();
                        const answered = r.status?.toLowerCase() === "answered";
                        const isInbound = dir === "inbound";
                        const isMissed = !answered;
                        const iconColor = isMissed ? "#ef4444" : isInbound ? "#10b981" : "#6366f1";
                        const dur = formatCallDuration(r.duration);
                        const isPlaying = numHistoryPlayingKey === i;
                        const isLoadingAudio = numHistoryAudioLoading === i;

                        return (
                          <div key={i}>
                            <div style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "8px 8px", borderRadius: 10, marginBottom: 4,
                              background: "rgba(255,255,255,0.04)",
                            }}>
                              {/* Direction icon */}
                              <div style={{
                                width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                                background: `${iconColor}18`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}>
                                {isMissed ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2"/>
                                    <line x1="17" y1="1" x2="23" y2="7"/><line x1="23" y1="1" x2="17" y2="7"/>
                                  </svg>
                                ) : isInbound ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2"/>
                                    <polyline points="17 8 13 8 13 12"/>
                                  </svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2"/>
                                    <line x1="14" y1="9" x2="20" y2="3"/><polyline points="14 3 20 3 20 9"/>
                                  </svg>
                                )}
                              </div>

                              {/* Info */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                  <span style={{ color: isMissed ? "#f87171" : "var(--dialer-text)", fontSize: 12, fontWeight: 600 }}>
                                    {isMissed ? "Missed" : isInbound ? "Incoming" : "Outgoing"}
                                  </span>
                                  {dur && <span style={{ color: "#64748b", fontSize: 10 }}>· {dur}</span>}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
                                  <span style={{ color: "#64748b", fontSize: 10 }}>{dayjs(r.start_time).format("MMM D, h:mm A")}</span>
                                  {r.ownerName && <span style={{ color: "#64748b", fontSize: 10 }}>· {r.ownerName}</span>}
                                </div>
                              </div>

                              {/* Recording button */}
                              {answered && r.record_file && (
                                <button
                                  onClick={() => handleNumHistoryRecording(r.record_file, i)}
                                  title={isPlaying ? "Stop" : "Play recording"}
                                  style={{
                                    width: 26, height: 26, borderRadius: "50%", border: "none", flexShrink: 0,
                                    background: isPlaying ? "rgba(99,102,241,0.4)" : "rgba(99,102,241,0.15)",
                                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                  }}
                                >
                                  {isLoadingAudio
                                    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2.5" style={{ animation: "spin 1s linear infinite" }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
                                    : isPlaying
                                      ? <svg width="10" height="10" viewBox="0 0 24 24" fill="#818cf8"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                      : <svg width="10" height="10" viewBox="0 0 24 24" fill="#818cf8"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                  }
                                </button>
                              )}
                            </div>

                            {/* Inline audio player */}
                            {isPlaying && numHistoryAudioUrl && (
                              <div style={{ margin: "0 0 5px 38px", padding: "5px 8px", background: "rgba(99,102,241,0.1)", borderRadius: 7, borderLeft: "2px solid #6366f1" }}>
                                <audio key={numHistoryAudioUrl} controls autoPlay style={{ width: "100%", height: 28, accentColor: "#6366f1" }}
                                  onEnded={() => { setNumHistoryAudioUrl(null); setNumHistoryPlayingKey(null); }}>
                                  <source src={numHistoryAudioUrl} />
                                </audio>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Pagination footer */}
                    {!numHistoryLoading && numHistoryTotal > 0 && (
                      <div style={{
                        flexShrink: 0, borderTop: "1px solid rgba(255,255,255,0.07)",
                        padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}>
                        <span style={{ color: "var(--dialer-subtext)", fontSize: 11 }}>
                          {numHistoryTotal} call{numHistoryTotal !== 1 ? "s" : ""}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <button
                            onClick={() => fetchNumberHistory(selectedCallDetail.number, numHistoryPage - 1)}
                            disabled={numHistoryPage === 1}
                            style={{
                              width: 24, height: 24, borderRadius: 6, border: "none",
                              background: "rgba(255,255,255,0.08)", color: "var(--dialer-text)",
                              cursor: numHistoryPage === 1 ? "not-allowed" : "pointer",
                              opacity: numHistoryPage === 1 ? 0.4 : 1,
                              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                            }}
                          >‹</button>
                          <span style={{ color: "var(--dialer-subtext)", fontSize: 11, minWidth: 44, textAlign: "center" }}>
                            {numHistoryPage} / {Math.ceil(numHistoryTotal / NUM_PAGE_SIZE)}
                          </span>
                          <button
                            onClick={() => fetchNumberHistory(selectedCallDetail.number, numHistoryPage + 1)}
                            disabled={numHistoryPage >= Math.ceil(numHistoryTotal / NUM_PAGE_SIZE)}
                            style={{
                              width: 24, height: 24, borderRadius: 6, border: "none",
                              background: "rgba(255,255,255,0.08)", color: "var(--dialer-text)",
                              cursor: numHistoryPage >= Math.ceil(numHistoryTotal / NUM_PAGE_SIZE) ? "not-allowed" : "pointer",
                              opacity: numHistoryPage >= Math.ceil(numHistoryTotal / NUM_PAGE_SIZE) ? 0.4 : 1,
                              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                            }}
                          >›</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Pagination + View All ── */}
                {!recentCallsLoading && recentTotal > 0 && (
                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                    {/* Prev / page info / Next */}
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <button
                        onClick={() => handleRecentPageChange(recentPage - 1)}
                        disabled={recentPage === 1}
                        style={{
                          width: 26, height: 26, borderRadius: 6, border: "none",
                          background: "rgba(255,255,255,0.08)", color: "var(--dialer-text)",
                          cursor: recentPage === 1 ? "not-allowed" : "pointer", opacity: recentPage === 1 ? 0.4 : 1,
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                        }}
                      >‹</button>
                      <span style={{ color: "var(--dialer-subtext)", fontSize: 11, minWidth: 60, textAlign: "center" }}>
                        {recentPage} / {Math.ceil(recentTotal / PAGE_SIZE)}
                        <span style={{ color: "#64748b" }}> · {recentTotal}</span>
                      </span>
                      <button
                        onClick={() => handleRecentPageChange(recentPage + 1)}
                        disabled={recentPage >= Math.ceil(recentTotal / PAGE_SIZE)}
                        style={{
                          width: 26, height: 26, borderRadius: 6, border: "none",
                          background: "rgba(255,255,255,0.08)", color: "var(--dialer-text)",
                          cursor: recentPage >= Math.ceil(recentTotal / PAGE_SIZE) ? "not-allowed" : "pointer",
                          opacity: recentPage >= Math.ceil(recentTotal / PAGE_SIZE) ? 0.4 : 1,
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                        }}
                      >›</button>
                    </div>
                    {/* View All */}
                    <button
                      onClick={() => {
                        const dest = userProfile?.role === "user" ? route.agentCallAndRecording : route.callAndRecording;
                        navigate(dest);
                        dispatch(closeDialer());
                      }}
                      style={{
                        display: "flex", alignItems: "center", gap: 4,
                        background: "rgba(99,102,241,0.15)", border: "none", borderRadius: 7,
                        padding: "5px 10px", cursor: "pointer", color: "#818cf8", fontSize: 11, fontWeight: 600,
                      }}
                    >
                      View All
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </button>
                  </div>
                )}
              </div>
            ) : (
            <>
            <div className="mb-3">
              {/* Extension toggle */}
              <div
                style={{
                  marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 14px", background: "rgba(255,255,255,0.05)", borderRadius: "12px", backdropFilter: "blur(10px)",
                }}
              >
                <span style={{ color: "var(--dialer-text)", fontSize: "14px", fontWeight: 500, letterSpacing: "0.5px" }}>
                  Dial Extension
                </span>
                <div
                  onClick={() => setIsManualDial(!isManualDial)}  
                  style={{
                    width: "40px", height: "20px",
                    background: isManualDial ? "var(--toggle-bg-on)" : "var(--toggle-bg-off)",
                    borderRadius: "20px", position: "relative", cursor: "pointer", transition: "all 0.3s ease",
                    display: "flex", alignItems: "center", padding: "1px",
                  }}
                >
                  <div
                    style={{
                      width: "18px", height: "18px",
                      background: "var(--toggle-circle)", borderRadius: "50%",
                      transform: isManualDial ? "translateX(22px)" : "translateX(0px)",
                      transition: "all 0.3s ease", boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    }}
                  />
                </div>
              </div>

              {isManualDial ? (
                <div className="d-flex align-items-center">
                  {/* <input
                    type="text"
                    value={manualNumber}
                    maxLength={4}
                    onChange={(e) => setManualNumber(e.target.value.replace(/[^0-9*#]/g, ""))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); handleDialpadCall(); return; }
                      if (!/[0-9*#]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter number"
                    style={{
                      width: "100%", background: "transparent",
                      border: "1px solid rgba(255,255,255,0.2)", color: "var(--dialer-text)",
                      fontSize: "18px", padding: "5px", borderRadius: "8px", outline: "none",
                      boxShadow: "0px 4px 4px 0px rgba(219,219,219,0.25)",
                    }}
                  /> */}


                  <input
                    type="text"
                    value={manualNumber}
                    maxLength={4}
                    onChange={(e) =>
                      setManualNumber(
                        e.target.value
                          .replace(/[^0-9*#]/g, "")
                          .slice(0, 4)
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleDialpadCall();
                        return;
                      }
                      if (
                        !/[0-9*#]/.test(e.key) &&
                        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter number"
                    style={{
                      width: "100%",
                      background: "transparent",
                      border: "1px solid rgba(255,255,255,0.2)",
                      color: "var(--dialer-text)",
                      fontSize: "18px",
                      padding: "5px",
                      borderRadius: "8px",
                      outline: "none",
                      boxShadow: "0px 4px 4px 0px rgba(219,219,219,0.25)",
                    }}
                  />
                  <button
                    onClick={handleDialpadClear}
                    style={{
                      width: "42px", height: "42px", background: "rgba(239,68,68,0.1)", border: "none",
                      borderRadius: "50%", color: "#ef4444", fontSize: "24px", cursor: "pointer",
                      display: manualNumber ? "flex" : "none", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, marginLeft: "10px",
                    }}
                  >⌫</button>
                </div>
              ) : (
                <div className="d-flex align-items-center">
                  <PhoneInput
                    country="ae"
                    value={phoneNumber}
                    onChange={(value, country, e) => {
                      // When calling from a local-format number (04XXXXXXXX),
                      // strip the 971 country code and add leading 0 automatically
                      const callerDigits = (selectedCallerNumber || "").replace(/\D/g, "");
                      if (callerDigits.startsWith("0") && value.startsWith("971") && value.length >= 10) {
                        setPhoneNumber("0" + value.slice(3));
                      } else {
                        setPhoneNumber(value);
                      }
                      if (e?.target) e.target.scrollLeft = e.target.scrollWidth;
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleDialpadCall(); } }}
                    inputStyle={{
                      width: "100%", background: "transparent", border: "none",
                      color: "var(--dialer-text)", fontSize: "18px", fontWeight: "300",
                      outline: "none", whiteSpace: "nowrap", overflowX: "auto", textOverflow: "clip",
                    }}
                  />
                  <button
                    onClick={handleDialpadClear}
                    style={{
                      width: "42px", height: "42px", background: "rgba(239,68,68,0.1)", border: "none",
                      borderRadius: "50%", color: "#ef4444", fontSize: "24px", cursor: "pointer",
                      display: phoneNumber ? "flex" : "none", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, marginLeft: "10px",
                    }}
                  >⌫</button>
                </div>
              )}
            </div>

            {/* Dialpad grid */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", width: "100%", justifyContent: "space-between" }}>
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((num) => (
                <button
                  key={num}
                  onClick={() => handleSendDTMF(num)}
                  style={{
                    padding: "10px", background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(94,94,94,0.15)",
                    borderRadius: "50%", color: "var(--dialer-text)",
                    width: "70px", height: "70px", cursor: "pointer",
                    display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.target.style.background = "rgba(255,255,255,0.15)"; e.target.style.transform = "scale(1.05)"; }}
                  onMouseLeave={(e) => { e.target.style.background = "rgba(255,255,255,0.05)"; e.target.style.transform = "scale(1)"; }}
                >
                  <span style={{ fontSize: "28px", fontWeight: "400", lineHeight: "1" }}>{num}</span>
                </button>
              ))}
            </div>

            {/* Call button */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "8px" }}>
              <button
                onClick={handleDialpadCall}
                disabled={!phoneNumber || isDialpadCalling}
                style={{
                  width: isDialpadCalling ? "auto" : "60px", height: "60px",
                  padding: isDialpadCalling ? "0 20px" : 0,
                  background: phoneNumber ? "#10b981" : "rgba(255,255,255,0.1)",
                  border: "none", borderRadius: isDialpadCalling ? 30 : "50%", color: "white", fontSize: isDialpadCalling ? 14 : "28px",
                  cursor: phoneNumber && !isDialpadCalling ? "pointer" : "not-allowed",
                  boxShadow: phoneNumber ? "0 8px 20px rgba(16,185,129,0.4)" : "none",
                  transition: "all 0.2s", opacity: phoneNumber ? 1 : 0.5,
                  fontWeight: 600, whiteSpace: "nowrap",
                }}
              >
                {isDialpadCalling
                  ? "Dialing..."
                  : <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z" />
                    </svg>
                }
              </button>
            </div>
            </>
            )}
          </div>
        )}
      </div>

      <audio ref={remoteAudioRef} autoPlay style={{ display: "none" }} />
      <AfterCallOffcanvas phoneNumber={callPhoneNumber} firstName={firstName} lastName={lastName} callStartedAt={callStartedAt} onAfterSave={powerDialer.isActive ? handlePowerDialerAfterSave : undefined} />
      {isMobile && (
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
          <div style={{ width: 36, height: 4, background: "rgba(255,255,255,0.25)", borderRadius: 2 }} />
        </div>
      )}
    </div>}

    {/* ── Caller dropdown portal — rendered at body level to escape overflow:hidden ── */}
    {callerDropdownOpen && ReactDOM.createPortal(
      <div
        ref={callerPortalRef}
        style={{
          position: "fixed",
          top: callerDropdownPos.top,
          left: callerDropdownPos.left,
          width: callerDropdownPos.width,
          zIndex: 2147483647,
          background: isDarkMode ? "#1a2133" : "rgb(248, 249, 250)",
          border: `1.5px solid ${isDarkMode ? "rgba(255,255,255,0.12)" : "#d1d5db"}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          minWidth: 180,
        }}
      >
        {callerNumbers.map((n) => {
          const isSelected = selectedCallerNumber === n.number;
          const isDefault  = userProfile?.defaultCallerNumber === n.number;
          const extStatus  = n.isExtension
            ? (extStatuses[n.extensionNumber] || "connecting")
            : (phoneStatus === "ready" ? "ready" : phoneStatus === "disconnected" ? "failed" : "connecting");
          const dotColor   = extStatus === "ready" ? "#22c55e" : extStatus === "failed" ? "#ef4444" : "#f59e0b";
          const textColor  = isDarkMode ? (isSelected ? "#a5b4fc" : "#ffffff") : (isSelected ? "#374151" : "#111827");
          const selBg      = isDarkMode ? "rgba(99,102,241,0.20)" : "#eef0f3";
          const hoverBg    = isDarkMode ? "rgba(255,255,255,0.07)" : "#f0f2f5";
          const divider    = isDarkMode ? "rgba(255,255,255,0.06)" : "#eaecef";
          const starInactive = isDarkMode ? "rgba(255,255,255,0.22)" : "#cbd5e1";
          return (
            <div
              key={n.number}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "9px 12px",
                background: isSelected ? selBg : "transparent",
                borderBottom: `1px solid ${divider}`,
                cursor: "pointer", transition: "background 0.1s",
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = hoverBg; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0, boxShadow: extStatus === "ready" ? `0 0 5px ${dotColor}` : "none" }} title={extStatus === "ready" ? "Connected" : extStatus === "failed" ? "Failed" : "Connecting…"} />
              <span
                onClick={() => { setSelectedCallerNumber(n.number); setCallerDropdownOpen(false); }}
                style={{ flex: 1, fontSize: "13px", color: textColor, fontWeight: isSelected ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {getCallerLabel(n)}
              </span>
              {n.isExtension && extStatus !== "ready" && (
                <button
                  onClick={(e) => { e.stopPropagation(); if (extStatus !== "connecting") retryExtensionInit(n.extensionNumber); }}
                  title={extStatus === "connecting" ? "Connecting…" : "Retry connection"}
                  style={{
                    background: "none", border: "none",
                    cursor: extStatus === "connecting" ? "default" : "pointer",
                    padding: "2px 3px", fontSize: "15px", lineHeight: 1, flexShrink: 0,
                    color: extStatus === "failed" ? "#ef4444" : "#f59e0b",
                    animation: extStatus === "connecting" ? "spin 1s linear infinite" : "none",
                    display: "inline-block",
                  }}
                >↺</button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const next = isDefault ? null : n.number;
                  api.patch("/editProfile/caller-preferences", { defaultCallerNumber: next })
                    .then(() => dispatch(setDefaultCallerNumber(next)))
                    .catch(() => {});
                }}
                title={isDefault ? "Remove default" : "Set as default"}
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: "2px 3px",
                  fontSize: "16px", lineHeight: 1, flexShrink: 0,
                  color: isDefault ? "#fbbf24" : starInactive,
                  transition: "color 0.15s, transform 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = isDefault ? "#f59e0b" : "rgba(251,191,36,0.55)"; e.currentTarget.style.transform = "scale(1.2)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = isDefault ? "#fbbf24" : starInactive; e.currentTarget.style.transform = "scale(1)"; }}
              >★</button>
            </div>
          );
        })}
      </div>,
      document.body
    )}
    </>
  );
}